/* eslint-disable no-unused-vars */
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import {
  calculateArchMidlineOptimized,
  calculateMedialDistalPointsOptimized,
  calculatePivotLinePoints,
  clusterizeMeshByNormals,
  createColoredMeshByCluster,
} from "@/utils/dentalCalculations";

import {
  findDentalAxes,
  // plotDentalAxes,
  plotOcclusalPlane,
  computeOcclusalPlaneFrom3Points
} from "@/utils/new_pca-utils";

import { analisarOrientacaoDenteFinal } from "@/utils/exemplo_uso_final"

import gsap from "gsap";

// eslint-disable-next-line no-unused-vars
import JSZip from "jszip";
import { PCA } from "ml-pca";

import {
  computeVertexNeighbors,
  createControlRings,
  createSpatialHash,
  detectBoundaries,
  groupBoundaryEdges,
  fillLoopsIntoMesh,
} from "@/utils/meshProcessing";

let originalMatrices = new Map();
// eslint-disable-next-line no-unused-vars
let currentMatrices = new Map();

let toothClickPoints = {};

// eslint-disable-next-line no-unused-vars
const NEIGHBOR_OFFSETS = [
  [0, 0, 0],
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];

// Exemplo de tabela com limites por dente (ajuste conforme sua necessidade)
// eslint-disable-next-line no-unused-vars
const LIMITES_POR_DENTE = {
  1: { rotacao: 1.5, translacao: 0.2 },
  2: { rotacao: 1.5, translacao: 0.2 },
  16: { rotacao: 1.5, translacao: 0.2 },
  17: { rotacao: 1.5, translacao: 0.2 },
  default: { rotacao: 1.5, translacao: 0.2 }, // Valor padrão para dentes não especificados
};

export default {
  methods: {
    mapToothNumberToFDI(sequentialNumber) {
      if (sequentialNumber >= 1 && sequentialNumber <= 8) {
        return 19 - sequentialNumber;
      } else if (sequentialNumber >= 9 && sequentialNumber <= 16) {
        return 20 + (sequentialNumber - 8);
      } else if (sequentialNumber >= 17 && sequentialNumber <= 24) {
        return 55 - sequentialNumber;
      } else if (sequentialNumber >= 25 && sequentialNumber <= 32) {
        return sequentialNumber + 16;
      }
      return sequentialNumber; // Retorna o próprio número se estiver fora do range esperado
    },
    calculateCenterOfMass(geometry) {
      const positionAttribute = geometry.attributes.position;
      const center = new THREE.Vector3();
      const array = positionAttribute.array;
      const numVertices = positionAttribute.count;

      if (numVertices === 0) return center;

      for (let i = 0; i < array.length; i += 3) {
        center.x += array[i];
        center.y += array[i + 1];
        center.z += array[i + 2];
      }

      center.divideScalar(numVertices);
      return center;
    },
    computeOBBFromMesh(mesh) {
      // 1) pontos em world‐space
      const posAttr = mesh.geometry.attributes.position;
      const worldMat = new THREE.Matrix4().copy(mesh.matrixWorld);
      const points = [];
      for (let i = 0; i < posAttr.count; i++) {
        const v = new THREE.Vector3()
          .fromBufferAttribute(posAttr, i)
          .applyMatrix4(worldMat);
        points.push([v.x, v.y, v.z]);
      }
      if (!points.length) return null;

      // 2) PCA
      const pca = new PCA(points);
      const mean = pca.means; // [mx, my, mz]
      const eMat = pca.getEigenvectors(); // Matrix do ml-matrix

      // 2.1) converte em Array puro
      const evecs =
        typeof eMat.to2DArray === "function"
          ? eMat.to2DArray()
          : Array.isArray(eMat.data)
          ? eMat.data
          : eMat; // caso já seja array

      // 3) extrai cada vetor e normaliza
      const [v0, v1] = evecs;
      const axisX = new THREE.Vector3(...v0).normalize();
      let axisY = new THREE.Vector3(...v1).normalize();
      // força ortogonalidade
      const axisZ = axisX.clone().cross(axisY).normalize();
      axisY.copy(axisZ.clone().cross(axisX)).normalize();

      // 4) monta matrizes
      const rotMat = new THREE.Matrix4().makeBasis(axisX, axisY, axisZ);
      const invRot = new THREE.Matrix4().copy(rotMat).invert();
      const trans = new THREE.Matrix4().makeTranslation(
        -mean[0],
        -mean[1],
        -mean[2]
      );
      const toLocal = new THREE.Matrix4().multiplyMatrices(invRot, trans);

      // 5) projeta no local, calcula AABB
      const localPts = points.map((p) => {
        const v = new THREE.Vector3(p[0], p[1], p[2]);
        return v.applyMatrix4(toLocal);
      });
      const box3 = new THREE.Box3().setFromPoints(localPts);
      const min = box3.min,
        max = box3.max;

      // 6) halfSize e centro local
      const halfSize = new THREE.Vector3(
        (max.x - min.x) / 2,
        (max.y - min.y) / 2,
        (max.z - min.z) / 2
      );
      const centerLocal = new THREE.Vector3(
        (max.x + min.x) / 2,
        (max.y + min.y) / 2,
        (max.z + min.z) / 2
      );

      // 7) centro em world
      const centerWorld = centerLocal
        .clone()
        .applyMatrix4(rotMat)
        .add(new THREE.Vector3(mean[0], mean[1], mean[2]));

      return { center: centerWorld, halfSize, rotMat };
    },
    createOBB(mesh, show = false, color = 0x8888ff) {
      const obb = this.computeOBBFromMesh(mesh);
      if (!obb) return null;

      // BoxGeometry espera full‐size (não half‐size)
      const fullSize = obb.halfSize.clone().multiplyScalar(2);
      const geo = new THREE.BoxGeometry(fullSize.x, fullSize.y, fullSize.z);
      const mat = new THREE.MeshBasicMaterial({ color, wireframe: true });
      const box = new THREE.Mesh(geo, mat);

      // Aplica rotação e posicionamento
      const m = new THREE.Matrix4().copy(obb.rotMat);
      m.setPosition(obb.center);
      box.applyMatrix4(m);

      if (show) {
        this.scene.add(box);
        // } else {
        // const { clusterMap } = clusterizeMeshByNormals(mesh, 6);
        // mesh.geometry.userData.meshOriginal = mesh;
        // const coloredMesh = createColoredMeshByCluster(
        //   mesh.geometry,
        //   clusterMap
        // );
        // this.scene.add(coloredMesh);
      }

      return box;
    },

    populateDentalMapFromLoadedMeshes(
      upperDentalMeshGroup,
      lowerDentalMeshGroup
    ) {
      const dentalMap = {};
      const upperTeethMeshes = {};
      const lowerTeethMeshes = {};

      // 1) coleta inicial e centerOfMass
      const processArcadeGroup = (arcadeGroup, teethMeshesMap) => {
        arcadeGroup.children.forEach((mesh) => {
          if (mesh.isMesh && mesh.userData.objectType === "tooth") {
            const key = mesh.userData.toothNumber;
            const localCOM = this.calculateCenterOfMass(mesh.geometry);
            const worldCOM = localCOM.clone().applyMatrix4(mesh.matrixWorld);
            dentalMap[key] = {
              borderPoints: [],
              borderTriangleIndices: [],
              segmentedTriangleIndices: [],
              medial: null,
              distal: null,
              centerOfMass: worldCOM,
              pivotLinePoints: [],
              obb: null,
              mesh: mesh,
              dentalAxes: null, // ← vamos preencher depois
            };
            teethMeshesMap[key] = mesh;
          }
        });
      };

      processArcadeGroup(upperDentalMeshGroup, upperTeethMeshes);
      processArcadeGroup(lowerDentalMeshGroup, lowerTeethMeshes);

      // 2) defina a ordem completa de cada arcada
      const toothOrderUpperRight = [
        "11",
        "12",
        "13",
        "14",
        "15",
        "16",
        "17",
        "18",
      ];
      const toothOrderUpperLeft = [
        "21",
        "22",
        "23",
        "24",
        "25",
        "26",
        "27",
        "28",
      ];
      const toothOrderLowerRight = [
        "41",
        "42",
        "43",
        "44",
        "45",
        "46",
        "47",
        "48",
      ];
      const toothOrderLowerLeft = [
        "31",
        "32",
        "33",
        "34",
        "35",
        "36",
        "37",
        "38",
      ];

      // 3) calcule centróides e normal oclusal de cada arcada
      const buildCentroids = (keys) =>
        keys
          .filter((k) => dentalMap[k])
          .map((k) => dentalMap[k].centerOfMass.clone());

      const upperKeys = [...toothOrderUpperRight, ...toothOrderUpperLeft];
      const upperCentroidsLocal = buildCentroids(upperKeys);
      const { plane : occlusionUpperPlane, normal : upperOcclusalNormal, center : upperOcclusalCenter }  =
        computeOcclusalPlaneFrom3Points(dentalMap, upperTeethMeshes, "11", "21", "16");
      //plotOcclusalPlane(this.scene, upperOcclusalNormal, upperOcclusalCenter, 80, 0xff00ff);

      const lowerKeys = [...toothOrderLowerRight, ...toothOrderLowerLeft];
      const lowerCentroidsLocal = buildCentroids(lowerKeys);
      const {plane : occlusionLowerPlane, normal : lowerOcclusalNormal, center : lowerOcclusalCenter} =
        computeOcclusalPlaneFrom3Points(dentalMap, lowerTeethMeshes, "31", "41", "46");
      //plotOcclusalPlane(this.scene, lowerOcclusalNormal, lowerOcclusalCenter, 80, 0xff000);

      // 4) arch midlines (mantém seu código)
      let upperArchMidlinePoint = null;
      if (upperTeethMeshes["11"] && upperTeethMeshes["21"]) {
        upperArchMidlinePoint = calculateArchMidlineOptimized(
          upperTeethMeshes["11"],
          upperTeethMeshes["21"]
        );
        upperDentalMeshGroup.userData.archMidlinePoint = upperArchMidlinePoint;
      }
      let lowerArchMidlinePoint = null;
      if (lowerTeethMeshes["31"] && lowerTeethMeshes["41"]) {
        lowerArchMidlinePoint = calculateArchMidlineOptimized(
          lowerTeethMeshes["31"],
          lowerTeethMeshes["41"]
        );
        lowerDentalMeshGroup.userData.archMidlinePoint = lowerArchMidlinePoint;
      }

      // 5) loops de medial/distal e obb (exemplo para arcada superior direita)
      const processSide = (
        keys, // ex: upperKeys ou lowerKeys
        meshMap, // ex: upperTeethMeshes ou lowerTeethMeshes
        archMidPoint, // ex: upperArchMidlinePoint ou lowerArchMidlinePoint
        centroids, // centroidsLocal correspondente
        occlusalNorm // occlusalNormal correspondente
      ) => {

        keys.forEach((toothKey, i) => {
          const mesh = meshMap[toothKey];
          if (!mesh) return;

          // medial/distal otimizado
          const prevMesh = meshMap[keys[i - 1]] || null;
          const nextMesh = meshMap[keys[i + 1]] || null;
          const { medialPoint, distalPoint, middlePoint } =
            calculateMedialDistalPointsOptimized(
              mesh,
              toothKey,
              archMidPoint,
              prevMesh,
              nextMesh,
              1.0
            );

          const entry = dentalMap[toothKey];
          entry.medial = medialPoint;
          entry.distal = distalPoint;
          entry.middlePoint = middlePoint;

          // pivotLine e OBB
          // entry.pivotLinePoints = calculatePivotLinePoints(
          //   this.scene,
          //   mesh,
          //   entry.centerOfMass,
          //   medialPoint,
          //   distalPoint,
          //   occlusionUpperPlane
          // );
          //  entry.pivotLinePoints = calculatePivotLinePoints(
          //   entry.centerOfMass,
          //   medialPoint,
          //   distalPoint
          // );
          //entry.obb = this.createOBB(mesh, false);

          //analisarOrientacaoDenteFinal(mesh, this.scene)

          // // ** aqui adicionamos os eixos anatômicos via PCA adaptado **
          // const axisIndex = keys.indexOf(toothKey);
          // entry.dentalAxes = findDentalAxes(centroids, axisIndex, occlusalNorm);

          // mesh.geometry.computeBoundingSphere();
          // const len = mesh.geometry.boundingSphere.radius * 0.8;

          // plotDentalAxes(
          //   this.scene,
          //   mesh,
          //   entry.centerOfMass,
          //   entry.dentalAxes,
          //   len
          // );
        });
      };

      // processamento de cada lado
      processSide(
        toothOrderUpperRight,
        upperTeethMeshes,
        upperArchMidlinePoint,
        upperCentroidsLocal,
        upperOcclusalNormal
      );
      processSide(
        toothOrderUpperLeft,
        upperTeethMeshes,
        upperArchMidlinePoint,
        upperCentroidsLocal,
        upperOcclusalNormal
      );
      processSide(
        toothOrderLowerRight,
        lowerTeethMeshes,
        lowerArchMidlinePoint,
        lowerCentroidsLocal,
        lowerOcclusalNormal
      );
      processSide(
        toothOrderLowerLeft,
        lowerTeethMeshes,
        lowerArchMidlinePoint,
        lowerCentroidsLocal,
        lowerOcclusalNormal
      );

      return dentalMap;
    },
    async load3szFileUnified(file) {
      const zip = new JSZip();
      const zipData = await zip.loadAsync(file);

      // Identificar o rootFolder e os caminhos dos arquivos
      const firstFile = Object.keys(zipData.files)[0];
      const rootFolder = firstFile.split("\\")[0];
      if (!rootFolder)
        return { upperDentalMeshGroup: null, lowerDentalMeshGroup: null };

      const patientName = rootFolder.replace(/\/$/, "");
      const modelsPath = `${patientName}\\setup\\Models\\`;

      let toothClickPoints = {};
      const toothClickPointsFile = `${patientName}/setup/Models/toothClickPoints.json`;
      if (zipData.files[toothClickPointsFile]) {
        try {
          const jsonText = await zipData.files[toothClickPointsFile].async(
            "string"
          );
          toothClickPoints = JSON.parse(jsonText);
          console.log(
            "toothClickPoints carregado com sucesso:",
            toothClickPoints
          );
        } catch (error) {
          console.warn("Erro ao carregar toothClickPoints.json:", error);
        }
      } else {
        console.log("toothClickPoints.json não encontrado no pacote.");
      }

      const upperJawFile = `${modelsPath}Tooth_UpperJaw.stl`;
      const lowerJawFile = `${modelsPath}Tooth_LowerJaw.stl`;
      const toothFiles = [];

      for (let i = 1; i <= 32; i++) {
        const toothPath = `${modelsPath}Tooth_${i}.stl`;
        if (zipData.files[toothPath]) {
          toothFiles.push(toothPath);
        }
      }

      // Criar materiais consistentes com createDentalMeshGroup
      const materialTeeth = new THREE.MeshPhysicalMaterial({
        color: 0xf7f7eb, // Cor base dos dentes (bege claro)
        roughness: 0.2, // Superfície lisa, mas não perfeitamente reflexiva
        metalness: 0.0, // Não metálico
        transmission: 0.4, // Translucidez
        ior: 1.4, // Índice de refração para simular vidro/dentes
        clearcoat: 1.0, // Camada de verniz para brilho extra
        clearcoatRoughness: 0.1, // Verniz suave
        envMapIntensity: 1.0, // Intensidade do reflexo ambiental
        flatShading: false, // Suaviza a superfície
      });

      // Material da gengiva usando MeshPhysicalMaterial para consistência
      const materialGum = new THREE.MeshPhysicalMaterial({
        color: 0xd4c2bc, // Cor base da gengiva (tom rosado)
        roughness: 0.7, // Superfície mais opaca
        metalness: 0.0, // Não metálico
        sheen: 0.5, // Efeito de subsuperfície (SSS)
        sheenColor: 0xfcc2c2, // Cor do efeito SSS (rosa claro)
        sheenRoughness: 0.6, // Rugosidade do efeito SSS
        envMapIntensity: 0.5, // Reflexo ambiental mais suave
        flatShading: false, // Suaviza a superfície
        clearcoat: 1.0, // Camada de verniz para brilho extra
        clearcoatRoughness: 0.1, // Verniz suave
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1,
      });

      // Criar Mesh Groups separados para arcada superior e inferior
      const upperDentalMeshGroup = new THREE.Group();
      upperDentalMeshGroup.name = "UpperDentalMeshGroup";
      upperDentalMeshGroup.userData = {
        modelType: "maxillary",
        name: this.$t("maxillary"),
        hide: false,
      };

      const lowerDentalMeshGroup = new THREE.Group();
      lowerDentalMeshGroup.name = "LowerDentalMeshGroup";
      lowerDentalMeshGroup.userData = {
        modelType: "mandibular",
        name: this.$t("mandibular"),
        hide: false,
      };

      const loader = new STLLoader();

      // Carregar e adicionar dentes aos seus respectivos grupos
      for (const filePath of toothFiles) {
        const stlData = await zipData.files[filePath].async("arraybuffer");
        const geometry = loader.parse(stlData);
        this.calculateVertexNormals(geometry); // Se necessário, ajuste para ser uma função externa ou passada como parâmetro

        const toothNumberMatch = filePath.match(/Tooth_(\d+)\.stl/);
        const sequentialToothNumber = toothNumberMatch
          ? parseInt(toothNumberMatch[1])
          : null;

        if (sequentialToothNumber !== null) {
          const fdiToothNumber = this.mapToothNumberToFDI(
            sequentialToothNumber
          );

          const toothMesh = new THREE.Mesh(geometry, materialTeeth.clone());
          toothMesh.userData = {
            objectType: "tooth",
            toothNumber: String(fdiToothNumber), // Usar a numeração FDI mapeada
            category: "tooth",
          };

          //const boxHelper = new THREE.BoxHelper(toothMesh, 0x9999ff)

          // Reposiciona cada dente para ter o centro de massa na origem, se a função centerGeometry for adaptada
          if (this.centerGeometry) {
            this.centerGeometry(geometry, toothMesh);
          }

          // Adicionar o dente ao grupo correto (superior ou inferior)
          if (sequentialToothNumber >= 1 && sequentialToothNumber <= 16) {
            upperDentalMeshGroup.add(toothMesh);
            //upperDentalMeshGroup.add(boxHelper);
          } else if (
            sequentialToothNumber >= 17 &&
            sequentialToothNumber <= 32
          ) {
            lowerDentalMeshGroup.add(toothMesh);
            //upperDentalMeshGroup.add(boxHelper);
          }
        }
      }

      // Carregar e adicionar gengivas aos seus respectivos grupos
      // Gengiva Superior
      if (zipData.files[upperJawFile]) {
        const upperGumData = await zipData.files[upperJawFile].async(
          "arraybuffer"
        );
        const geometry = loader.parse(upperGumData);
        geometry.computeVertexNormals();

        // As funções de processamento de gengiva (detectBoundaries, groupBoundaryEdges, fillLoopsIntoMesh, etc.)
        // precisariam ser adaptadas para serem funções auxiliares ou passadas como dependências.

        const gumMesh = new THREE.Mesh(geometry, materialGum.clone());
        gumMesh.name = "upperGum";
        gumMesh.userData = {
          objectType: "gums",
          category: "gums",
          arcada: "superior",
          // originalVertices: gumObject.originalVertices,
        };

        upperDentalMeshGroup.add(gumMesh);
      }

      // Gengiva Inferior
      if (zipData.files[lowerJawFile]) {
        const lowerGumData = await zipData.files[lowerJawFile].async(
          "arraybuffer"
        );
        const geometry = loader.parse(lowerGumData);
        geometry.computeVertexNormals();

        const gumMesh = new THREE.Mesh(geometry, materialGum.clone());
        gumMesh.name = "lowerGum";
        gumMesh.userData = {
          objectType: "gums",
          category: "gums",
          arcada: "inferior",
          // originalVertices: gumObject.originalVertices,
        };

        lowerDentalMeshGroup.add(gumMesh);
      }

      this.dentalMap = this.populateDentalMapFromLoadedMeshes(
        upperDentalMeshGroup,
        lowerDentalMeshGroup
      );

      // Retorna um objeto contendo os dois grupos de arcadas
      this.scene.add(upperDentalMeshGroup);
      this.scene.add(lowerDentalMeshGroup);
      this.$store.dispatch("editor3D/updateScene", this.scene);
      this.sceneUpdate = new Date();
      this.$store.dispatch("editor3D/updateDate", this.sceneUpdate);
      return { upperDentalMeshGroup, lowerDentalMeshGroup };
    },
    abrirDialog(tempoInicial) {
      this.tempoTratamento = tempoInicial;
      this.tempoOriginal = tempoInicial;
      this.dialogAjuste = true;
    },
    ajustarMeses(delta) {
      const novoTempo = this.tempoTratamento + delta;
      if (novoTempo >= 1) {
        this.tempoTratamento = novoTempo;
      }
    },
    confirmarTempo() {
      this.dialogAjuste = false;
      this.gerarPlanoTratamento(
        this.tempoTratamento,
        originalMatrices,
        currentMatrices
      );
    },
    translateToothNameToNotation(toothName) {
      const toothNumber = parseInt(toothName.split("_")[1]);

      let toothInQuadrant, toothLabelKey, toothNumberAdjusted;

      if (toothNumber >= 1 && toothNumber <= 8) {
        toothInQuadrant = toothNumber;
        toothNumberAdjusted = 19 - toothInQuadrant;
      } else if (toothNumber >= 9 && toothNumber <= 16) {
        toothInQuadrant = toothNumber - 8;
        toothNumberAdjusted = 20 + toothInQuadrant;
      } else if (toothNumber >= 17 && toothNumber <= 24) {
        toothInQuadrant = toothNumber - 16;
        toothNumberAdjusted = 39 - toothInQuadrant;
      } else if (toothNumber >= 25 && toothNumber <= 32) {
        toothInQuadrant = toothNumber - 24;
        toothNumberAdjusted = 40 + toothInQuadrant;
      }

      const toothInQuadrantFinal = toothNumberAdjusted % 10;

      switch (toothInQuadrantFinal) {
        case 1:
          toothLabelKey = "teeth.centralIncisor";
          break;
        case 2:
          toothLabelKey = "teeth.lateralIncisor";
          break;
        case 3:
          toothLabelKey = "teeth.canine";
          break;
        case 4:
        case 5:
          toothLabelKey = "teeth.premolar";
          break;
        case 6:
          toothLabelKey = "teeth.firstMolar";
          break;
        case 7:
          toothLabelKey = "teeth.secondMolar";
          break;
        case 8:
          toothLabelKey = "teeth.thirdMolar";
          break;
        default:
          toothLabelKey = "teeth.unknown";
      }

      return `${toothNumberAdjusted} - ${this.$t(toothLabelKey)}`;
    },
    updateHUD(toothPivot) {
      const toothMesh = toothPivot.children.find((obj) =>
        obj.name.startsWith("Tooth_")
      );

      if (toothMesh) {
        this.selectedToothInfo = {
          name: toothPivot.name,
          position: toothMesh.getWorldPosition(new THREE.Vector3()).toArray(),
          rotation: toothPivot.quaternion.toArray(),
          scale: toothMesh.scale.toArray(),
        };
      }
    },
    getNumeroDente(objeto) {
      const partes = objeto.name.split("_"); // "toothPivot_Tooth_15" → ["toothPivot", "Tooth", "15"]
      return partes[partes.length - 1]; // Retorna "15"
    },
    async generateTreatmentPlanFromCurrentState(userCycles = null) {
      // 1) Coleta o estado atual dos dentes na cena
      currentMatrices = new Map();
      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          const toothMesh = child.children.find((o) =>
            o.name.startsWith("Tooth_")
          );
          if (!toothMesh) return;
          currentMatrices.set(child.uuid, {
            toothPosition: toothMesh.getWorldPosition(new THREE.Vector3()),
            position: child.position.clone(),
            quaternion: child.quaternion.clone(),
            scale: child.scale.clone(),
          });
        }
      });

      // 2) Garante que temos originalMatrices gravadas em algum momento de inicialização
      //    (por exemplo, no carregamento do modelo)
      if (!originalMatrices) {
        console.error(
          "originalMatrices não está definido. Deve capturá‑lo ao carregar o modelo."
        );
        return;
      }

      // 3) Decide quantos ciclos usar:
      //    - se o usuário passou um número, usa esse (modo fixo)
      //    - senão, calcula o mínimo necessário (modo livre)
      let numCiclos;
      if (typeof userCycles === "number" && userCycles > 0) {
        numCiclos = userCycles;
      } else {
        const base = this.calcularTempoTratamento(
          originalMatrices,
          currentMatrices
        );
        numCiclos = Math.ceil(base);
      }

      this.abrirDialog(numCiclos);

      // 4) Chama gerarPlanoTratamento (numCiclos, matrizInicial, matrizFinal)
      //    matrizInicial = original; matrizFinal = current
      // this.gerarPlanoTratamento(
      //     numCiclos,
      //     originalMatrices,
      //     currentMatrices
      // );
    },
    async applyRandomRotations(maxAngle = Math.PI / 30) {
      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          const toothPivotGroup = child; // O grupo pivot do dente
          const isShiftPressed = Math.random() > 0.5; // Simula aleatoriamente o Shift pressionado

          let rotationAxis;
          if (isShiftPressed && toothPivotGroup.userData.pivotAxis) {
            // 🛠️ Modo GIRO PURO: Usa o eixo salvo no userData (como na rotação via Scroll)
            rotationAxis = toothPivotGroup.userData.pivotAxis
              .clone()
              .normalize();
          } else {
            // 🎥 Modo LIVRE: Usa eixo fixo baseado na direção da câmera
            const cameraDirection = this.camera.getWorldDirection(
              new THREE.Vector3()
            );
            const absX = Math.abs(cameraDirection.x);
            const absY = Math.abs(cameraDirection.y);
            const absZ = Math.abs(cameraDirection.z);

            rotationAxis = new THREE.Vector3();
            if (absX <= absY && absX <= absZ) {
              rotationAxis.set(1, 0, 0); // Eixo X global
            } else if (absY <= absX && absY <= absZ) {
              rotationAxis.set(0, 1, 0); // Eixo Y global
            } else {
              rotationAxis.set(0, 0, 1); // Eixo Z global
            }

            // Converter eixo para espaço LOCAL do dente
            toothPivotGroup.localToWorld(rotationAxis);
            toothPivotGroup.worldToLocal(rotationAxis);
            rotationAxis.normalize();
          }

          const angle = THREE.MathUtils.randFloat(0.1, maxAngle);
          const rotation = new THREE.Quaternion().setFromAxisAngle(
            rotationAxis,
            angle
          );

          if (isShiftPressed) {
            toothPivotGroup.quaternion.multiply(rotation); // 🔄 Rotação LOCAL (em torno do próprio eixo)
          } else {
            toothPivotGroup.quaternion.premultiply(rotation); // 🔄 Rotação GLOBAL (baseada na câmera)
          }

          toothPivotGroup.updateMatrixWorld(true);
        }
      });

      // Após aplicar rotações, capture o estado atualizado
      currentMatrices.clear();
      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          const toothMesh = child.children.find((obj) =>
            obj.name.startsWith("Tooth_")
          );

          if (toothMesh) {
            currentMatrices.set(child.uuid, {
              toothPosition: toothMesh.getWorldPosition(new THREE.Vector3()),
              position: child.position.clone(),
              quaternion: child.quaternion.clone(),
              scale: child.scale.clone(),
            });
          }
        }
      });

      const tempoTotal = Math.ceil(
        this.calcularTempoTratamento(currentMatrices, originalMatrices)
      );
      this.gerarPlanoTratamento(tempoTotal, currentMatrices, originalMatrices);

      // Garantir renderização antes de capturar os estados
      await new Promise((resolve) => requestAnimationFrame(resolve));
    },
    calcularTempoTratamento(matrizInicial, matrizFinal) {
      const tempoPorDente = new Map();

      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          const numeroDente = this.getNumeroDente(child);
          const limites =
            LIMITES_POR_DENTE[numeroDente] || LIMITES_POR_DENTE.default;

          const inicial = matrizInicial.get(child.uuid);
          const final = matrizFinal.get(child.uuid);

          // Cálculo da diferença de rotação (ângulo entre quaternions)
          const anguloRot = inicial.quaternion.angleTo(final.quaternion);

          // Cálculo da diferença de translação (distância entre posições)
          const deltaPos = new THREE.Vector3()
            .subVectors(final.toothPosition, inicial.toothPosition)
            .length();

          // Tempo necessário para este dente (considerando limites específicos)
          const tempoRot = anguloRot / limites.rotacao;
          const tempoTrans = deltaPos / limites.translacao;
          const tempoTotal = Math.max(tempoRot, tempoTrans);

          tempoPorDente.set(child.uuid, tempoTotal);
        }
      });

      // O tempo total do tratamento é o máximo entre todos os dentes
      return Math.max(...Array.from(tempoPorDente.values()));
    },
    gerarPlanoTratamento(numCiclos, matrizInicial, matrizFinal) {
      const plano = new Map();

      let numeroDente;

      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          numeroDente = this.getNumeroDente(child);

          const inicial = matrizInicial.get(child.uuid);
          const final = matrizFinal.get(child.uuid);

          const matrizesCiclo = [];
          for (let ciclo = 0; ciclo <= numCiclos; ciclo++) {
            const progresso = ciclo / numCiclos;
            const eased = THREE.MathUtils.smootherstep(progresso, 0, 1);

            // Interpolação considerando os limites do dente
            const pos = new THREE.Vector3().lerpVectors(
              inicial.position,
              final.position,
              eased
            );

            const quat = new THREE.Quaternion().slerpQuaternions(
              inicial.quaternion,
              final.quaternion,
              eased
            );

            matrizesCiclo.push({ posicao: pos, quaternion: quat });
          }

          plano.set(numeroDente, matrizesCiclo);
        }
      });

      this.totalCycles = numCiclos;
      this.currentCycle = 0;

      // Fazer uma cópia profunda para evitar referências compartilhadas
      this.planoTratamentoOriginal = new Map(
        [...plano].map(([key, value]) => [
          key,
          value.map((v) => ({
            posicao: new THREE.Vector3().copy(v.posicao),
            quaternion: new THREE.Quaternion().copy(v.quaternion),
          })),
        ])
      );

      this.planoTratamento = new Map(
        [...plano].map(([key, value]) => [
          key,
          value.map((v) => ({
            posicao: new THREE.Vector3().copy(v.posicao),
            quaternion: new THREE.Quaternion().copy(v.quaternion),
          })),
        ])
      );

      return plano;
    },
    animateToOriginal() {
      if (this.isAnimating) return;
      this.isAnimating = true;
      this.animationStartTime = performance.now();

      const animate = () => {
        const elapsed = performance.now() - this.animationStartTime;
        const progress = Math.min(elapsed / this.animationDuration, 1);
        const eased = THREE.MathUtils.smootherstep(progress, 0, 1);

        // calcula índice fracionário de ciclo
        const cycleF = eased * this.totalCycles;
        const low = Math.floor(cycleF);
        const high = Math.min(Math.ceil(cycleF), this.totalCycles);
        const t = cycleF - low;

        this.scene.traverse((child) => {
          if (!child.name.startsWith("toothPivot_")) return;

          const num = this.getNumeroDente(child);
          const arr = this.planoTratamento.get(num);
          if (!arr) return;

          const lowT = arr[low];
          const highT = arr[high];
          if (!lowT || !highT) return;

          // interpola posição e rotação suavemente
          child.position.lerpVectors(lowT.posicao, highT.posicao, t);
          child.quaternion.copy(lowT.quaternion).slerp(highT.quaternion, t);
          child.updateMatrixWorld(true);

          if (this.selectedToothInfo?.name === child.name) {
            this.updateHUD(child);
          }
        });

        this.renderer.render(this.scene, this.camera);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.isAnimating = false;
        }
      };

      animate();
    },
    pauseAnimation() {
      this.isAnimating = false;
    },
    async resetAnimation() {
      await this.applyRandomRotations();
      this.animationProgress = 0;
      this.animateToOriginal();
    },
    toggleJawVisibility(upperVisible, lowerVisible, showPivotLines = true) {
      const upper = this.scene.getObjectByName("upperJaw");
      const lower = this.scene.getObjectByName("lowerJaw");
      const pivotGroup = this.scene.getObjectByName("pivotGroup"); // Buscar o grupo que contém os dentes

      if (upper) upper.visible = upperVisible;
      if (lower) lower.visible = lowerVisible;

      if (pivotGroup) {
        pivotGroup.children.forEach((toothPivot) => {
          const match = toothPivot.name.match(/toothPivot_Tooth_(\d+)/);
          if (match) {
            const toothNumber = parseInt(match[1], 10);
            const isUpperTooth = toothNumber >= 1 && toothNumber <= 16;
            const isLowerTooth = toothNumber >= 17 && toothNumber <= 32;

            // Controla a visibilidade do grupo inteiro do dente
            const toothVisible = isUpperTooth
              ? upperVisible
              : isLowerTooth
              ? lowerVisible
              : false;
            toothPivot.visible = toothVisible;

            // Percorre os meshes dentro do toothPivot
            toothPivot.children.forEach((mesh) => {
              if (mesh.name.startsWith("Tooth_")) {
                mesh.visible = toothVisible; // Mostrar/ocultar o dente real
              } else {
                mesh.visible = showPivotLines; // Mostrar/ocultar os elementos da linha de pivot
              }
            });
          }
        });
      }
    },
    updateTreatmentPlanAfterManualAdjustment(toothNumber, adjustedCycle) {
      if (
        this.planoTratamento.size === 0 ||
        this.currentCycle === 0 ||
        adjustedCycle === 0
      )
        return;
      const treatmentPlanOriginal =
        this.planoTratamentoOriginal.get(toothNumber);
      const treatmentPlan = this.planoTratamento.get(toothNumber);
      const adjustmentKey = `${toothNumber}_${adjustedCycle}`;
      const adjustment = this.manualAdjustments.get(adjustmentKey);
      if (!adjustment) return;

      // 1) Aplica o ajuste no ciclo manual
      treatmentPlan[adjustedCycle] = {
        posicao: adjustment.newTransform.position.clone(),
        quaternion: adjustment.newTransform.quaternion.clone(),
      };

      // Pontos de referência no plano original
      const originalStart = treatmentPlanOriginal[0];
      const originalEnd = treatmentPlanOriginal[this.totalCycles];

      // 2) Interpola os ciclos ANTERIORES ao manual
      //    - do ciclo 1 até adjustedCycle-1
      for (let i = 1; i < adjustedCycle; i++) {
        const progresso = i / adjustedCycle;
        const eased = THREE.MathUtils.smootherstep(progresso, 0, 1);

        if (!treatmentPlan[i]) {
          treatmentPlan[i] = {
            posicao: new THREE.Vector3(),
            quaternion: new THREE.Quaternion(),
          };
        }

        // posição: do START original → ao ponto de ajuste
        treatmentPlan[i].posicao = new THREE.Vector3().lerpVectors(
          originalStart.posicao,
          adjustment.newTransform.position,
          eased
        );

        // rotação: do START original → ao quat de ajuste
        treatmentPlan[i].quaternion = new THREE.Quaternion().slerpQuaternions(
          originalStart.quaternion,
          adjustment.newTransform.quaternion,
          eased
        );
      }

      // 3) Interpola os ciclos SUBSEQUENTES ao manual (como já estava)
      const remainingCycles = this.totalCycles - adjustedCycle;
      for (let i = 1; i <= remainingCycles; i++) {
        const ciclo = adjustedCycle + i;
        const progresso = i / remainingCycles;
        const eased = THREE.MathUtils.smootherstep(progresso, 0, 1);

        if (!treatmentPlan[ciclo]) {
          treatmentPlan[ciclo] = {
            posicao: new THREE.Vector3(),
            quaternion: new THREE.Quaternion(),
          };
        }

        // posição: do ponto de ajuste → ao END original
        treatmentPlan[ciclo].posicao = new THREE.Vector3().lerpVectors(
          adjustment.newTransform.position,
          originalEnd.posicao,
          eased
        );

        // rotação: do quat de ajuste → ao quat END original
        treatmentPlan[ciclo].quaternion =
          new THREE.Quaternion().slerpQuaternions(
            adjustment.newTransform.quaternion,
            originalEnd.quaternion,
            eased
          );
      }

      // 4) Atualiza a cena no ciclo atual
      this.updateSceneForCycle_(this.currentCycle);
    },
    removeManualAdjustment(key) {
      if (!this.manualAdjustments.has(key)) {
        console.warn(`Ajuste manual ${key} não encontrado.`);
        return;
      }

      const [toothNumber, cycle] = key.split("_");

      // Remover o ajuste da lista
      this.manualAdjustments.delete(key);
      console.log(`Ajuste manual ${key} removido.`);

      // Restaurar os dados originais no plano de tratamento **a partir do ciclo afetado até o final**
      if (this.planoTratamentoOriginal.has(toothNumber)) {
        const originalPlan = this.planoTratamentoOriginal.get(toothNumber);
        const updatedPlan = new Map(this.planoTratamento);

        if (!originalPlan[cycle]) {
          console.error(
            `Ciclo ${cycle} não encontrado em planoTratamentoOriginal para o dente ${toothNumber}.`
          );
          return;
        }

        // Verifica se o dente existe no planoTratamento antes de modificar
        if (!updatedPlan.has(toothNumber)) {
          updatedPlan.set(toothNumber, []);
        }

        const restoredTransformations = updatedPlan.get(toothNumber);

        for (let i = cycle; i <= this.totalCycles; i++) {
          if (originalPlan[i]) {
            restoredTransformations[i] = {
              posicao: new THREE.Vector3().copy(originalPlan[i].posicao),
              quaternion: new THREE.Quaternion().copy(
                originalPlan[i].quaternion
              ),
            };
          }
        }

        // Atualiza this.planoTratamento para garantir que Vue detecte a mudança
        this.planoTratamento = updatedPlan;

        console.log(
          `Plano restaurado do ciclo ${cycle} em diante para o dente ${toothNumber}.`
        );
      } else {
        console.error(
          `Dente ${toothNumber} não encontrado em planoTratamentoOriginal.`
        );
      }

      // Atualizar a cena para refletir a remoção do ajuste
      console.log(`Chamando updateSceneForCycle(${cycle})`);
      this.updateSceneForCycle_(parseInt(cycle));
    },
    findAllAffectedGumVertuces(gumMesh, toothMeshes, scaleFactor = 1.1) {
      const preprocessedTeeth = toothMeshes
        .map((tooth) => {
          const result = this.createScaledTooth(tooth, scaleFactor);
          if (!result) return null;

          const { clonedTooth } = result;
          clonedTooth.geometry.computeBoundsTree();

          return {
            mesh: clonedTooth,
            cleanup: result.cleanup,
            bbox: new THREE.Box3().setFromObject(clonedTooth),
          };
        })
        .filter(Boolean);

      this.scene.updateMatrixWorld(true);
      gumMesh.updateMatrixWorld(true);
      preprocessedTeeth.forEach(({ mesh }) => mesh.updateMatrixWorld(true));

      const { hash, cellSize } = this.gumMorphing[gumMesh.name].spatialHash;
      const positions = gumMesh.geometry.attributes.position.array;
      const gumMatrixWorld = gumMesh.matrixWorld.clone();
      const inverseGumMatrixWorld = gumMatrixWorld.clone().invert();

      const affected = new Set();

      // Calcula a bounding box combinada dos dentes escalados em espaço mundial
      const globalBBox = new THREE.Box3();
      preprocessedTeeth.forEach(({ bbox }) => globalBBox.union(bbox));
      const localBBox = globalBBox.clone().applyMatrix4(inverseGumMatrixWorld);

      // Calcula os índices das células mínima e máxima na grade espacial
      const minCellX = Math.floor(localBBox.min.x / cellSize);
      const maxCellX = Math.floor(localBBox.max.x / cellSize);
      const minCellY = Math.floor(localBBox.min.y / cellSize);
      const maxCellY = Math.floor(localBBox.max.y / cellSize);
      const minCellZ = Math.floor(localBBox.min.z / cellSize);
      const maxCellZ = Math.floor(localBBox.max.z / cellSize);

      // Itera sobre os índices das células e verifica vértices
      for (let cx = minCellX; cx <= maxCellX; cx++) {
        for (let cy = minCellY; cy <= maxCellY; cy++) {
          for (let cz = minCellZ; cz <= maxCellZ; cz++) {
            const key = cx + cy * 1e4 + cz * 1e8; // Mesmo formato da chave da grade
            if (hash[key]) {
              hash[key].forEach((vertexIdx) => {
                const localGumVertex = new THREE.Vector3(
                  positions[vertexIdx * 3],
                  positions[vertexIdx * 3 + 1],
                  positions[vertexIdx * 3 + 2]
                );

                const worldVertex = localGumVertex
                  .clone()
                  .applyMatrix4(gumMatrixWorld);

                // Verifica se o vértice está dentro de algum dente escalado
                for (const { mesh } of preprocessedTeeth) {
                  if (this.isPointInsideMesh_(worldVertex, mesh)) {
                    affected.add(vertexIdx);
                    break; // Early exit para este vértice
                  }
                }
              });
            }
          }
        }
      }

      preprocessedTeeth.forEach(({ cleanup }) => cleanup());

      return {
        affectedVertices: Array.from(affected),
      };
    },
    findToothMeshInsidePivot(pivotGroup) {
      let toothMesh = null;

      pivotGroup.traverse((child) => {
        if (child.isMesh && child.name.startsWith("Tooth_")) {
          toothMesh = child;
        }
      });

      return toothMesh;
    },
    updateSceneForCycle(cycle) {
      // 🔁 RESETAR GENGIVA

      console.warn("Reseta gengiva");
      if (this.gumMorphing) {
        for (const gumName in this.gumMorphing) {
          const gumData = this.gumMorphing[gumName];
          const positions = gumData.mesh.geometry.attributes.position.array;
          const rest = gumData.restPositions;
          for (let i = 0; i < positions.length; i++) {
            positions[i] = rest[i];
          }
          gumData.mesh.geometry.attributes.position.needsUpdate = true;
        }
      }

      const upperTeeth = [];
      const lowerTeeth = [];

      // Atualizar pivôs dos dentes e agrupar os meshes
      console.warn("Atualiza posição dentes");

      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          const numeroDente = this.getNumeroDente(child);
          const transformations = this.planoTratamento.get(numeroDente);

          if (transformations && transformations[cycle]) {
            const { posicao, quaternion } = transformations[cycle];
            child.position.copy(posicao);
            child.quaternion.copy(quaternion);
            child.updateMatrixWorld(true);

            if (this.selectedToothInfo?.name === child.name) {
              this.updateHUD(child);
            }
          }

          const toothMesh = this.findToothMeshInsidePivot(child);
          if (toothMesh) {
            // Agrupa os dentes por arcada
            if (numeroDente <= 16) {
              upperTeeth.push(toothMesh);
            } else {
              lowerTeeth.push(toothMesh);
            }
          }
        }
      });

      // Agora atualizamos a gengiva da arcada superior de uma vez
      const upperGum = this.scene.getObjectByName("upper");
      if (upperGum && upperTeeth.length > 0) {
        console.warn("Procura vertices afetados arcada superior");

        const upperAffected = this.findAllAffectedGumVertuces(
          upperGum,
          upperTeeth,
          1.15
        );
        console.warn("Deforma Gengiva SUperior");

        this.showDebugPoints(
          upperGum,
          upperAffected.affectedVertices,
          0x0000ff
        );
        this.applyGumDeformationByNormalsWithHash(
          "upper",
          upperAffected.affectedVertices,
          0.25 // intensidade
        );
        upperGum.geometry.attributes.position.needsUpdate = true;
        console.warn("Concluida deformacao");
      }

      // E o mesmo para a inferior
      const lowerGum = this.scene.getObjectByName("lower");
      if (lowerGum && lowerTeeth.length > 0) {
        console.warn("Procura vertices afetados arcada superior");
        const lowerAffected = this.findAllAffectedGumVertuces(
          lowerGum,
          lowerTeeth,
          1.15
        );
        console.warn("Deforma Gengiva Inferior");
        //this.showDebugPoints(lowerGum, lowerAffected.affectedVertices, 0xffff00);
        this.applyGumDeformationByNormalsWithHash(
          "lower",
          lowerAffected.affectedVertices,
          0.25 // intensidade
        );
        lowerGum.geometry.attributes.position.needsUpdate = true;
        console.warn("Concluida deformacao");
      }

      this.renderer.render(this.scene, this.camera);
    },
    updateSceneForCycle_(cycle) {
      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          const numeroDente = this.getNumeroDente(child);
          const transformations = this.planoTratamento.get(numeroDente);

          if (transformations && transformations[cycle]) {
            const { posicao, quaternion } = transformations[cycle];

            // Atualiza o pivot (que controla o dente)
            child.position.copy(posicao);
            child.quaternion.copy(quaternion);
            child.updateMatrixWorld(true);

            // Atualiza HUD se este dente estiver selecionado
            if (this.selectedToothInfo?.name === child.name) {
              this.updateHUD(child);
            }
          }
        }
      });

      this.renderer.render(this.scene, this.camera);
    },
    addRotationOnScroll() {
      window.addEventListener("wheel", (event) => {
        if (!this.currentlySelectedTooth) return;

        const delta = event.deltaY > 0 ? -0.01 : 0.01;
        const selectedTooth = this.scene.getObjectByName(
          this.currentlySelectedTooth
        );
        const toothPivotGroup = selectedTooth.parent;
        const toothNumber = this.currentlySelectedTooth.split("_")[1];

        // Recupera transform original para fallback
        const treatmentPlanOriginal =
          this.planoTratamentoOriginal.get(toothNumber);
        const fallbackTransform = {
          position: toothPivotGroup.position.clone(),
          quaternion: toothPivotGroup.quaternion.clone(),
        };
        const originalTransform =
          treatmentPlanOriginal && treatmentPlanOriginal[this.currentCycle]
            ? {
                position:
                  treatmentPlanOriginal[this.currentCycle].posicao.clone(),
                quaternion:
                  treatmentPlanOriginal[this.currentCycle].quaternion.clone(),
              }
            : fallbackTransform;

        // Modificadores
        const isAltPressed = event.getModifierState("Alt");
        const isShiftPressed = event.getModifierState("Shift");

        // 1) calcula eixo “livre” em world-space
        const camDir = this.camera.getWorldDirection(new THREE.Vector3());
        const absX = Math.abs(camDir.x),
          absY = Math.abs(camDir.y),
          absZ = Math.abs(camDir.z);

        const freeAxis = new THREE.Vector3();
        if (absX <= absY && absX <= absZ) {
          freeAxis.set(1, 0, 0);
        } else if (absY <= absX && absY <= absZ) {
          freeAxis.set(0, 1, 0);
        } else {
          freeAxis.set(0, 0, 1);
        }
        freeAxis.normalize();

        if (isAltPressed) {
          // ─── MODO TRANSLATE ───
          const translationFactor = 10;
          toothPivotGroup.position.add(
            freeAxis.clone().multiplyScalar(delta * translationFactor)
          );
        } else if (isShiftPressed) {
          // ─── MODO GIRO PURO ───
          toothPivotGroup.rotateOnAxis(
            toothPivotGroup.userData.pivotAxis.clone().normalize(),
            delta
          );
        } else {
          // ─── MODO LIVRE ───
          toothPivotGroup.rotateOnWorldAxis(freeAxis, delta);
        }

        toothPivotGroup.updateMatrixWorld(true);

        // Registra / atualiza o ajuste manual
        const adjustmentKey = `${toothNumber}_${this.currentCycle}`;
        const existingAdjustment = this.manualAdjustments.get(adjustmentKey);
        const newTransform = {
          position: toothPivotGroup.position.clone(),
          quaternion: toothPivotGroup.quaternion.clone(),
        };

        if (this.currentCycle !== 0) {
          if (existingAdjustment) {
            existingAdjustment.newTransform = newTransform;
          } else {
            this.manualAdjustments.set(adjustmentKey, {
              toothNumber,
              cycle: this.currentCycle,
              originalTransform: {
                position: originalTransform.position.clone(),
                quaternion: originalTransform.quaternion.clone(),
              },
              newTransform,
            });
          }
        }

        // Atualiza o plano de tratamento e renderiza
        this.updateTreatmentPlanAfterManualAdjustment(
          toothNumber,
          this.currentCycle
        );
        this.updateGumAfterToothMovement(selectedTooth, -delta);
        this.renderer.render(this.scene, this.camera);
      });
    },
    laplacianSmooth(geometry, iterations = 1, factor = 0.5) {
      const pos = geometry.attributes.position;
      const vertexCount = pos.count;
      const positions = [];
      for (let i = 0; i < vertexCount; i++) {
        positions.push(new THREE.Vector3().fromBufferAttribute(pos, i));
      }

      const neighborsMap = this.buildVertexNeighbors(geometry);

      for (let iter = 0; iter < iterations; iter++) {
        const newPositions = positions.map((v, idx) => {
          const neighbors = neighborsMap[idx];
          if (!neighbors || neighbors.length === 0) return v.clone();

          const avg = new THREE.Vector3();
          neighbors.forEach((n) => avg.add(positions[n]));
          avg.divideScalar(neighbors.length);

          return v.clone().lerp(avg, factor); // suaviza na direção da média
        });

        for (let i = 0; i < vertexCount; i++) {
          positions[i].copy(newPositions[i]);
          pos.setXYZ(i, positions[i].x, positions[i].y, positions[i].z);
        }
        pos.needsUpdate = true;
      }
    },
    buildVertexNeighbors(geometry) {
      const posAttr = geometry.attributes.position;
      const vertexCount = posAttr.count;
      const neighbors = {};

      for (let i = 0; i < vertexCount; i += 3) {
        const a = i;
        const b = i + 1;
        const c = i + 2;

        [
          [a, b],
          [b, c],
          [c, a],
        ].forEach(([v1, v2]) => {
          if (!neighbors[v1]) neighbors[v1] = new Set();
          if (!neighbors[v2]) neighbors[v2] = new Set();
          neighbors[v1].add(v2);
          neighbors[v2].add(v1);
        });
      }

      // converte sets para arrays
      Object.keys(neighbors).forEach((k) => {
        neighbors[k] = Array.from(neighbors[k]);
      });

      return neighbors;
    },
    displaceBothGumsAlongNormals(intensity = 0.01) {
      const jawNames = ["upper", "lower"];

      for (const jawName of jawNames) {
        const gumMesh = this.scene.getObjectByName(jawName);
        if (!gumMesh) continue;

        const geometry = gumMesh.geometry;
        const positionAttr = geometry.attributes.position;
        const normalAttr = geometry.attributes.normal;

        const vertex = new THREE.Vector3();
        const normal = new THREE.Vector3();

        for (let i = 0; i < positionAttr.count; i++) {
          vertex.fromBufferAttribute(positionAttr, i);
          normal.fromBufferAttribute(normalAttr, i);

          vertex.addScaledVector(normal, intensity);
          positionAttr.setXYZ(i, vertex.x, vertex.y, vertex.z);
        }

        positionAttr.needsUpdate = true;
      }
    },
    updateGumDisplacement(gumName, affectedVertices, intensities) {
      const gumMesh = this.scene.getObjectByName(gumName);
      if (!gumMesh) return;

      const dispAttr = gumMesh.geometry.attributes.displacement;
      dispAttr.array.fill(0);
      dispAttr.needsUpdate = true;

      const arr = dispAttr.array; // Float32Array

      // opcional: use updateRange para mínimo upload
      let minOff = Infinity,
        maxOff = -Infinity;

      for (let i = 0; i < affectedVertices.length; i++) {
        const idx = affectedVertices[i];
        const delta = Array.isArray(intensities) ? intensities[i] : intensities;

        arr[idx] += delta;

        if (idx < minOff) minOff = idx;
        if (idx > maxOff) maxOff = idx;
      }

      // marque o range manchado
      dispAttr.updateRange.offset = minOff;
      dispAttr.updateRange.count = maxOff - minOff + 1;
      dispAttr.needsUpdate = true;
    },
    displaceBothGumsOnAffected(affectedByJaw, intensities) {
      for (const jawName of ["upper", "lower"]) {
        const verts = affectedByJaw[jawName];
        if (verts && verts.length) {
          console.warn("inicio deformação");
          this.updateGumDisplacement(jawName, verts, intensities);
          console.warn("termino deformação");
        }
      }
    },
    async updateGumAfterToothMovement(toothMesh, delta) {
      // 1. Determinar arcada pelo nome do dente
      const toothNumber = parseInt(toothMesh.name.split("_")[1], 10);
      const isUpperJaw = toothNumber <= 16;
      const jawName = isUpperJaw ? "upper" : "lower";

      // 2. Obter gengiva correspondente
      const gumMesh = this.scene.getObjectByName(jawName);

      if (!gumMesh) return;

      const { affectedVertices, distances } = this.findAffectedGumVertices(
        gumMesh,
        toothMesh,
        1.5
      );

      // intensidades proporcionais à distância * sinal do delta
      const maxDist = Math.max(...distances, 1);
      const baseIntensity = 300; // ou o valor que você já vinha usando
      // um array de intensidades com sinal
      const intensities = distances.map((d) => {
        const fator = 1 - d / maxDist; // [0..1]
        return delta * baseIntensity * fator; // já traz o sinal
      });

      // supondo que jawName seja 'upper' ou 'lower':
      this.displaceBothGumsOnAffected(
        { [jawName]: affectedVertices },
        intensities
      );

      // Adicione visualização dos vértices afetados
      //this.showDebugPoints(gumMesh, affectedVertices, 0xffff00);

      gumMesh.geometry.attributes.position.needsUpdate = true;
    },
    applyGumDeformationUsingToothMatrix(
      gumMesh,
      affectedVertexIndices,
      transformationMatrix
    ) {
      const positionAttr = gumMesh.geometry.attributes.position;
      const vertex = new THREE.Vector3();

      const matrix = new THREE.Matrix4().copy(transformationMatrix);

      for (const index of affectedVertexIndices) {
        vertex.fromBufferAttribute(positionAttr, index);
        vertex.applyMatrix4(matrix);
        positionAttr.setXYZ(index, vertex.x, vertex.y, vertex.z);
      }

      positionAttr.needsUpdate = true;
    },
    showDebugPoints(gumMesh, verticesIndices, color = 0xffff00) {
      if (this.debugPoints) this.scene.remove(this.debugPoints);

      const geometry = gumMesh.geometry;
      const positions = geometry.attributes.position.array;

      const pointsGeometry = new THREE.BufferGeometry();
      const pointsPositions = [];

      verticesIndices.forEach((index) => {
        pointsPositions.push(
          positions[index * 3],
          positions[index * 3 + 1],
          positions[index * 3 + 2]
        );
      });

      pointsGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(pointsPositions, 3)
      );
      this.debugPoints = new THREE.Points(
        pointsGeometry,
        new THREE.PointsMaterial({ color, size: 1 })
      );
      this.scene.add(this.debugPoints);
    },
    findAffectedGumVertices(gumMesh, toothMesh, scaleFactor = 1.1) {
      const result = this.createScaledTooth(toothMesh, scaleFactor);
      if (!result)
        return { affectedVertices: [], distances: [], toothWorldPos: null };

      const { clonedTooth: scaledTooth, cleanup } = result;

      // Captura posição ORIGINAL do dente ANTES da transformação (chamada externa)
      const initialToothWorldPos = new THREE.Vector3();
      toothMesh.getWorldPosition(initialToothWorldPos);

      this.scene.updateMatrixWorld(true);
      scaledTooth.updateMatrixWorld(true);
      gumMesh.updateMatrixWorld(true);

      if (!scaledTooth.geometry.boundsTree) {
        scaledTooth.geometry.computeBoundsTree();
      }

      const { hash, cellSize } = this.gumMorphing[gumMesh.name].spatialHash;
      const positions = gumMesh.geometry.attributes.position.array;

      const toothBBox = new THREE.Box3().setFromObject(scaledTooth);
      const gumMatrixWorld = gumMesh.matrixWorld.clone().invert();
      const localBBox = toothBBox.clone().applyMatrix4(gumMatrixWorld);

      const minX = localBBox.min.x;
      const maxX = localBBox.max.x;
      const minY = localBBox.min.y;
      const maxY = localBBox.max.y;
      const minZ = localBBox.min.z;
      const maxZ = localBBox.max.z;

      const affectedVertices = [];
      const distances = [];
      const vertexCache = new Set();

      const getCellKey = (x, y, z) => {
        const cx = Math.floor(x / cellSize);
        const cy = Math.floor(y / cellSize);
        const cz = Math.floor(z / cellSize);
        return cx + cy * 1e4 + cz * 1e8;
      };

      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          for (let z = minZ; z <= maxZ; z++) {
            const key = getCellKey(x, y, z);
            if (hash[key]) {
              hash[key].forEach((vertexIdx) => {
                if (vertexCache.has(vertexIdx)) return;

                const localGumVertex = new THREE.Vector3(
                  positions[vertexIdx * 3],
                  positions[vertexIdx * 3 + 1],
                  positions[vertexIdx * 3 + 2]
                );

                const worldVertex = localGumVertex
                  .clone()
                  .applyMatrix4(gumMesh.matrixWorld);
                const inside = this.isPointInsideMesh_(
                  worldVertex,
                  scaledTooth
                );

                if (inside) {
                  // Calcula distância para a posição ORIGINAL do dente
                  const distance = worldVertex.distanceTo(initialToothWorldPos);

                  affectedVertices.push(vertexIdx);
                  distances.push(distance);
                  vertexCache.add(vertexIdx);
                }
              });
            }
          }
        }
      }

      const toothWorldPos = scaledTooth.getWorldPosition(new THREE.Vector3());
      cleanup();

      return {
        affectedVertices,
        distances,
        toothWorldPos,
      };
    },
    isPointInsideMesh_(pointWorld, mesh) {
      // 1. Confirma se a geometria tem BVH
      if (!mesh.geometry.boundsTree) {
        console.log("🧱 Criando BVH...");
        mesh.geometry.computeBoundsTree();
      }

      // 3. Ray em direção arbitrária (X positivo)
      const direction = new THREE.Vector3(1, 0, 0);

      const raycaster = new THREE.Raycaster();
      raycaster.firstHitOnly = false;
      raycaster.ray.origin.copy(pointWorld);
      raycaster.ray.direction.copy(direction);

      // 4. Intersecções com o mesh
      const intersects = raycaster.intersectObject(mesh, true);

      // 5. Regra de paridade
      return intersects.length % 2 !== 0;
    },
    createScaledTooth(originalTooth, scaleFactor) {
      // 1. Clona o dente individual
      const clonedTooth = originalTooth.clone(true);

      // 2. Clona e escala apenas a geometria, mantendo centrado
      const scaledGeometry = originalTooth.geometry.clone();

      // Calcula centro do bounding box da geometria original
      const bbox = new THREE.Box3().setFromBufferAttribute(
        scaledGeometry.attributes.position
      );
      const center = new THREE.Vector3();
      bbox.getCenter(center);

      // Translada para a origem, aplica escala e volta ao centro original
      const m1 = new THREE.Matrix4().makeTranslation(
        -center.x,
        -center.y,
        -center.z
      );
      const m2 = new THREE.Matrix4().makeScale(
        scaleFactor,
        scaleFactor,
        scaleFactor
      );
      const m3 = new THREE.Matrix4().makeTranslation(
        center.x,
        center.y,
        center.z
      );

      const transform = new THREE.Matrix4()
        .multiplyMatrices(m3, m2)
        .multiply(m1);
      scaledGeometry.applyMatrix4(transform);

      clonedTooth.geometry = scaledGeometry;

      // 3. Clona posição mundial (sem depender do grupo pai)
      const worldMatrix = new THREE.Matrix4();
      originalTooth.updateMatrixWorld(true);
      worldMatrix.copy(originalTooth.matrixWorld);

      // Aplica a matrixWorld ao objeto clonado
      clonedTooth.matrix.copy(worldMatrix);
      clonedTooth.matrix.decompose(
        clonedTooth.position,
        clonedTooth.quaternion,
        clonedTooth.scale
      );
      clonedTooth.matrixAutoUpdate = false;

      // 4. Torna invisível e adiciona à cena
      clonedTooth.visible = true;
      this.scene.add(clonedTooth);

      return {
        clonedTooth,
        cleanup: () => this.scene.remove(clonedTooth),
      };
    },
    applyGumDeformationOptimized(
      gumName,
      affectedIndices,
      intensity = 1.0,
      influenceRadius = 2.0
    ) {
      const gumData = this.gumMorphing[gumName];
      const {
        mesh: gumMesh,
        restPositions,
        spatialHash: { hash, cellSize },
      } = gumData;
      const posAttr = gumMesh.geometry.attributes.position;
      const positions = posAttr.array;
      const normals = gumMesh.geometry.attributes.normal.array;
      const radius2 = influenceRadius * influenceRadius;

      // Pré‑aloca vetores e buffers
      const tmpRest = new THREE.Vector3();
      const tmpNorm = new THREE.Vector3();
      const toReset = new Set();

      // 1) Antes de tudo, marque para resetar cada affected + vizinhos no raio
      for (const vi of affectedIndices) {
        // calcula célula do vértice base
        const rx = (restPositions[vi * 3] / cellSize) | 0;
        const ry = (restPositions[vi * 3 + 1] / cellSize) | 0;
        const rz = (restPositions[vi * 3 + 2] / cellSize) | 0;

        // percorre vizinhos de célula ±1
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            for (let dz = -1; dz <= 1; dz++) {
              const key = rx + dx + (ry + dy) * 1e4 + (rz + dz) * 1e8;
              const cell = hash[key];
              if (!cell) continue;
              for (const j of cell) {
                toReset.add(j);
              }
            }
          }
        }
        toReset.add(vi);
      }

      // 2) Resetar só esses vértices à posição de repouso
      for (const j of toReset) {
        positions[j * 3] = restPositions[j * 3];
        positions[j * 3 + 1] = restPositions[j * 3 + 1];
        positions[j * 3 + 2] = restPositions[j * 3 + 2];
      }

      // 3) Computar deslocamento para cada vértice afetado + vizinhos
      //    e acumular no atributo de posição
      for (const vi of affectedIndices) {
        // posição e normal originais
        tmpRest.set(
          restPositions[vi * 3],
          restPositions[vi * 3 + 1],
          restPositions[vi * 3 + 2]
        );
        tmpNorm
          .set(normals[vi * 3], normals[vi * 3 + 1], normals[vi * 3 + 2])
          .normalize();

        // deslocamento base
        const baseDisp = tmpNorm.clone().multiplyScalar(intensity);

        // aplica no próprio vértice
        positions[vi * 3] += baseDisp.x;
        positions[vi * 3 + 1] += baseDisp.y;
        positions[vi * 3 + 2] += baseDisp.z;

        // agora propaga suavemente aos vizinhos até influenceRadius
        const rx = (tmpRest.x / cellSize) | 0;
        const ry = (tmpRest.y / cellSize) | 0;
        const rz = (tmpRest.z / cellSize) | 0;

        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            for (let dz = -1; dz <= 1; dz++) {
              const key = rx + dx + (ry + dy) * 1e4 + (rz + dz) * 1e8;
              const cell = hash[key];
              if (!cell) continue;
              for (const j of cell) {
                if (j === vi) continue;

                // distância ao ponto central
                const x = restPositions[j * 3];
                const y = restPositions[j * 3 + 1];
                const z = restPositions[j * 3 + 2];
                const dx2 = x - tmpRest.x;
                const dy2 = y - tmpRest.y;
                const dz2 = z - tmpRest.z;
                const dist2 = dx2 * dx2 + dy2 * dy2 + dz2 * dz2;
                if (dist2 > radius2) continue;

                const falloff = 1 - Math.sqrt(dist2) / influenceRadius;

                // normal do vizinho
                tmpNorm
                  .set(normals[j * 3], normals[j * 3 + 1], normals[j * 3 + 2])
                  .normalize();

                // deslocamento suavizado
                const disp = tmpNorm
                  .clone()
                  .multiplyScalar(intensity * falloff);

                positions[j * 3] += disp.x;
                positions[j * 3 + 1] += disp.y;
                positions[j * 3 + 2] += disp.z;
              }
            }
          }
        }
      }

      // 4) sinaliza atualização
      posAttr.needsUpdate = true;
    },
    displaceGumVerticesAlongNormalsByDistance(
      gumName,
      affectedVertices,
      distances,
      baseIntensity = 0.01
    ) {
      const gumMesh = this.scene.getObjectByName(gumName);
      if (!gumMesh) return;

      const posAttr = gumMesh.geometry.attributes.position;
      const norAttr = gumMesh.geometry.attributes.normal;
      const vertex = new THREE.Vector3();
      const normal = new THREE.Vector3();

      // normaliza distâncias para [0..1]
      const maxDist = Math.max(...distances, 1);

      for (let i = 0; i < affectedVertices.length; i++) {
        const idx = affectedVertices[i];
        const dist = distances[i];

        // fator = 1 → vértice mais próximo; = 0 → mais distante
        const factor = 1 - dist / maxDist;
        const intensity = baseIntensity * factor;

        // lê posição e normal
        vertex.fromBufferAttribute(posAttr, idx);
        normal.fromBufferAttribute(norAttr, idx);

        // desloca
        vertex.addScaledVector(normal, intensity);
        posAttr.setXYZ(idx, vertex.x, vertex.y, vertex.z);
      }

      posAttr.needsUpdate = true;
    },
    applyGumDeformationByNormalsWithHash(
      gumName,
      affectedVertices,
      intensity = 1.0,
      tolerance = 1e-4
    ) {
      const gumData = this.gumMorphing[gumName];
      const gumMesh = gumData.mesh;
      const geometry = gumMesh.geometry;
      const restPositions = gumData.restPositions;
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal.array;
      const hash = gumData.spatialHash.hash;
      const cellSize = gumData.spatialHash.cellSize;

      const tolerance2 = tolerance * tolerance;
      const alreadyUpdated = new Uint8Array(positions.length / 3);

      // Reinicia os positions com a posição de repouso
      for (let i = 0; i < positions.length; i++) {
        positions[i] = restPositions[i];
      }

      for (const vertexIdx of affectedVertices) {
        if (alreadyUpdated[vertexIdx]) continue;

        const i = vertexIdx * 3;
        const originalX = restPositions[i];
        const originalY = restPositions[i + 1];
        const originalZ = restPositions[i + 2];

        const normalX = normals[i];
        const normalY = normals[i + 1];
        const normalZ = normals[i + 2];

        if (!isFinite(normalX) || !isFinite(normalY) || !isFinite(normalZ))
          continue;

        const cx = Math.floor(originalX / cellSize);
        const cy = Math.floor(originalY / cellSize);
        const cz = Math.floor(originalZ / cellSize);

        for (const [dx, dy, dz] of NEIGHBOR_OFFSETS) {
          const key = cx + dx + (cy + dy) * 1e4 + (cz + dz) * 1e8;

          const cell = hash[key];
          if (!cell) continue;

          for (const otherIdx of cell) {
            if (alreadyUpdated[otherIdx]) continue;

            const j = otherIdx * 3;
            const j0 = j,
              j1 = j + 1,
              j2 = j + 2;

            const candidateX = restPositions[j0];
            const candidateY = restPositions[j1];
            const candidateZ = restPositions[j2];

            const dx2 = (candidateX - originalX) ** 2;
            const dy2 = (candidateY - originalY) ** 2;
            const dz2 = (candidateZ - originalZ) ** 2;

            if (dx2 + dy2 + dz2 < tolerance2) {
              positions[j0] += normalX * intensity;
              positions[j1] += normalY * intensity;
              positions[j2] += normalZ * intensity;

              alreadyUpdated[otherIdx] = 1;
            }
          }
        }
      }
    },
    storeOriginalMatrices() {
      originalMatrices.clear();
      this.scene.traverse((child) => {
        if (child.name.startsWith("toothPivot_")) {
          // Encontra o dente dentro do grupo pivot
          const toothMesh = child.children.find((obj) =>
            obj.name.startsWith("Tooth_")
          );

          if (toothMesh) {
            // Salva posição GLOBAL do dente e transformações do pivot
            originalMatrices.set(child.uuid, {
              toothPosition: toothMesh.getWorldPosition(new THREE.Vector3()),
              position: child.position.clone(),
              quaternion: child.quaternion.clone(),
              scale: child.scale.clone(),
            });
          }
        }
      });
    },
    calculateToothNormal(denteMesh) {
      const geom = denteMesh.geometry;
      const posAttr = geom.attributes.position;
      const normalsArr = geom.attributes.normal?.array;
      const toothName = denteMesh.name;
      const match = toothName.match(/Tooth_(\d+)/);
      if (!match) return new THREE.Vector3(0, 1, 0);
      const num = parseInt(match[1], 10);

      // listas fixas
      const molares = [1, 2, 3, 14, 15, 16, 17, 18, 19, 30, 31, 32];
      const preM = [4, 5, 12, 13, 20, 21, 28, 29];

      // 1) molares e pré‑molares: média de normais (mantém igual)
      if (molares.includes(num) || preM.includes(num)) {
        let sum = new THREE.Vector3();
        for (let i = 0; i < normalsArr.length; i += 3) {
          sum.add(
            new THREE.Vector3(
              normalsArr[i],
              normalsArr[i + 1],
              normalsArr[i + 2]
            )
          );
        }
        return sum.normalize();
      }

      // 2) caninos/incisivos: “ponta” média dos vértices mais distantes
      // 2.1) centro em world‑space
      const center = new THREE.Vector3();
      for (let i = 0; i < posAttr.count; i++) {
        const v = new THREE.Vector3()
          .fromBufferAttribute(posAttr, i)
          .applyMatrix4(denteMesh.matrixWorld);
        center.add(v);
      }
      center.divideScalar(posAttr.count);

      // 2.2) calcula distâncias e armazena
      const list = [];
      for (let i = 0; i < posAttr.count; i++) {
        const w = new THREE.Vector3()
          .fromBufferAttribute(posAttr, i)
          .applyMatrix4(denteMesh.matrixWorld);
        list.push({ p: w, d: w.distanceTo(center) });
      }

      // 2.3) ordena do maior para o menor, pega top 10%
      list.sort((a, b) => b.d - a.d);
      const take = Math.max(Math.floor(list.length * 0.1), 3);
      const tip = new THREE.Vector3();
      for (let i = 0; i < take; i++) tip.add(list[i].p);
      tip.divideScalar(take);

      // 2.4) direção = tip – center
      const dir = tip.clone().sub(center).normalize();
      // inverte se for arcada inferior
      return dir.negate();
    },
    computeCentroid(denteMesh) {
      const pos = denteMesh.geometry.attributes.position;
      const c = new THREE.Vector3();
      for (let i = 0; i < pos.count; i++) {
        c.add(
          new THREE.Vector3()
            .fromBufferAttribute(pos, i)
            .applyMatrix4(denteMesh.matrixWorld)
        );
      }
      return c.divideScalar(pos.count);
    },
    computeTipPoint(denteMesh, percentile = 0.25) {
      const pos = denteMesh.geometry.attributes.position;
      const centro = this.computeCentroid(denteMesh);

      // calcula distâncias
      const arr = [];
      for (let i = 0; i < pos.count; i++) {
        const w = new THREE.Vector3()
          .fromBufferAttribute(pos, i)
          .applyMatrix4(denteMesh.matrixWorld);
        arr.push({ p: w, d: w.distanceTo(centro) });
      }

      // ordena decrescente e pega top-percentile
      arr.sort((a, b) => b.d - a.d);
      const take = Math.max(Math.floor(pos.count * percentile), 3);
      const sum = new THREE.Vector3();
      for (let i = 0; i < take; i++) sum.add(arr[i].p);

      return sum.divideScalar(take);
    },
    computePracticalPivot(denteMesh, percentile = 0.1) {
      const centro = this.computeCentroid(denteMesh);
      const tip = this.computeTipPoint(denteMesh, percentile);

      // anchor refletido
      const anchor = centro.clone().sub(tip.clone().sub(centro));
      const front = tip.clone();

      return { anchorPoint: anchor, frontPoint: front };
    },
    createPivotLine(denteMesh) {
      const toothName = denteMesh.name;

      // 🔁 0) Remove pivotGroup antigo (se existir)
      const oldPivot = this.scene.getObjectByName(`toothPivot_${toothName}`);
      if (oldPivot) {
        // 🔁 Recupera o dente original de dentro do grupo
        const originalTooth = oldPivot.getObjectByName(toothName);

        if (originalTooth) {
          // Reposiciona o dente de volta ao world space
          originalTooth.updateMatrixWorld(true);
          const worldPos = new THREE.Vector3();
          const worldQuat = new THREE.Quaternion();
          const worldScale = new THREE.Vector3();
          originalTooth.matrixWorld.decompose(worldPos, worldQuat, worldScale);

          const parent = oldPivot.parent;

          // Remove o grupo da cena
          parent.remove(oldPivot);
          oldPivot.traverse((obj) => {
            if (obj.geometry) obj.geometry.dispose();
            if (obj.material) {
              if (Array.isArray(obj.material))
                obj.material.forEach((m) => m.dispose());
              else obj.material.dispose();
            }
          });

          // Reinsere o dente original no parent correto
          originalTooth.position.copy(worldPos);
          originalTooth.quaternion.copy(worldQuat);
          originalTooth.scale.copy(worldScale);
          originalTooth.name = toothName; // garantir nome consistente
          parent.add(originalTooth);
        } else {
          console.warn(
            `Dente ${toothName} não encontrado dentro de ${oldPivot.name}`
          );
        }
      }

      // 1) centro do dente via boundingSphere (world‑space)
      const geo = denteMesh.geometry;
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      const centroDente = geo.boundingSphere.center
        .clone()
        .applyMatrix4(denteMesh.matrixWorld);

      // 2) vê se já temos clique manual
      const clicked = toothClickPoints?.[toothName];

      let anchorPoint, frontPoint;
      if (clicked) {
        // 2.1) frontCandidate = ponto clicado em world‑space
        const frontCand = new THREE.Vector3(clicked.x, clicked.y, clicked.z);

        // 2.2) eixo do “front” a partir do centro
        const v = frontCand.clone().sub(centroDente);
        const distCF = v.length();
        const dirCF = v.clone().normalize();

        // 2.3) âncora = simétrico ao frontCand em relação ao centro
        anchorPoint = centroDente
          .clone()
          .sub(dirCF.clone().multiplyScalar(distCF * 2));

        // 2.4) frontPoint = frontCand avançado um pouco adiante
        const frontOffset = 4; // ajuste fino conforme necessário
        frontPoint = frontCand
          .clone()
          .add(dirCF.clone().multiplyScalar(frontOffset));
      } else {
        // fallback: eixo pela normal do dente
        const normal = this.calculateToothNormal(denteMesh).normalize();
        const distanciaAncor = 15;
        const distanciaFrente = 8;
        anchorPoint = centroDente
          .clone()
          .add(normal.clone().multiplyScalar(distanciaAncor));
        frontPoint = centroDente
          .clone()
          .sub(normal.clone().multiplyScalar(distanciaFrente));
      }

      // 3) monta o group de pivô na anchorPoint
      const pivotGroup = new THREE.Group();
      pivotGroup.name = `toothPivot_${toothName}`;
      pivotGroup.position.copy(anchorPoint);
      pivotGroup.userData = {
        centroDente,
        pivotAxis: new THREE.Vector3()
          .subVectors(frontPoint, anchorPoint)
          .normalize(),
        manualClickPoint: clicked ? anchorPoint.clone() : null,
      };

      // 4) reparent do dente
      const parent = denteMesh.parent;
      parent.remove(denteMesh);
      const worldPos = new THREE.Vector3();
      denteMesh.getWorldPosition(worldPos);
      denteMesh.position.copy(worldPos.sub(anchorPoint));
      pivotGroup.add(denteMesh);

      // 5) desenha a linha do pivô
      const pts = [
        new THREE.Vector3(0, 0, 0),
        frontPoint.clone().sub(anchorPoint),
      ];
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: 0xff0000 })
      );
      line.name = `pivotLine_${toothName}`;
      pivotGroup.add(line);

      // 6) esferas de controle
      const sphGeo = new THREE.SphereGeometry(0.25);
      const sphAnc = new THREE.Mesh(
        sphGeo,
        new THREE.MeshBasicMaterial({ color: 0x0000ff })
      );
      const sphFro = new THREE.Mesh(
        sphGeo,
        new THREE.MeshBasicMaterial({ color: 0xff0000 })
      );
      sphAnc.name = `sphereAncoragem_${toothName}`;
      sphFro.name = `sphereFrente_${toothName}`;
      sphAnc.position.set(0, 0, 0);
      sphFro.position.copy(pts[1]);
      pivotGroup.add(sphAnc, sphFro);

      // 💡 Nova esfera no centro do dente
      const sphCentro = new THREE.Mesh(
        sphGeo,
        new THREE.MeshBasicMaterial({ color: 0xff00ff })
      );
      sphCentro.name = `sphereCentro_${toothName}`;
      // centroDente está em world-space, então ajustamos para o espaço local do pivotGroup (relativo ao anchorPoint)
      sphCentro.position.copy(centroDente.clone().sub(anchorPoint));
      pivotGroup.add(sphCentro);

      // 7) volta pra cena
      parent.add(pivotGroup);

      return pivotGroup;
    },
    getOrCreatePivotGroup() {
      let pivotGroup = this.scene.getObjectByName("pivotGroup");
      if (!pivotGroup) {
        pivotGroup = new THREE.Group();
        pivotGroup.name = "pivotGroup"; // Nome único para o grupo
        this.scene.add(pivotGroup); // Adiciona o grupo à cena
      }
      return pivotGroup;
    },
    addPivotLinesForAllTeeth() {
      // Busca os grupos de arcada superior e inferior
      const upperJawGroup = this.scene.getObjectByName("upperJaw");
      const lowerJawGroup = this.scene.getObjectByName("lowerJaw");

      // Se algum dos grupos não for encontrado, retorna
      if (!upperJawGroup || !lowerJawGroup) {
        console.error("Grupos de arcada não encontrados na cena.");
        return;
      }

      // Filtra os meshes dentro dos dois grupos de dentes com nome começando com "Tooth_"
      const upperTeethMeshes = upperJawGroup.children.filter(
        (child) =>
          child.name.startsWith("Tooth_") && child instanceof THREE.Mesh
      );
      const lowerTeethMeshes = lowerJawGroup.children.filter(
        (child) =>
          child.name.startsWith("Tooth_") && child instanceof THREE.Mesh
      );

      const isEmpty = Object.keys(toothClickPoints).length === 0;

      // Adiciona as linhas de pivô para cada mesh de dente
      upperTeethMeshes.forEach((denteMesh) => {
        const pivotGroup = this.getOrCreatePivotGroup();
        const pivotLine = isEmpty
          ? this.createPivotLineSimplified(denteMesh)
          : this.createPivotLine(denteMesh);
        pivotGroup.add(pivotLine);
      });

      lowerTeethMeshes.forEach((denteMesh) => {
        const pivotGroup = this.getOrCreatePivotGroup();
        const pivotLine = isEmpty
          ? this.createPivotLineSimplified(denteMesh)
          : this.createPivotLine(denteMesh);
        pivotGroup.add(pivotLine);
      });

      this.storeOriginalMatrices();
    },
    createPivotLineSimplified(
      denteMesh,
      distanciaFrente = 6,
      distanciaTras = 10
    ) {
      // 1) pega centro via boundingSphere em world-space
      const geo = denteMesh.geometry;
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      const centroWorld = geo.boundingSphere.center
        .clone()
        .applyMatrix4(denteMesh.matrixWorld);

      // 2) direção Y (+1 pra inferior, –1 pra superior 1–16)
      const direcao = new THREE.Vector3(0, 1, 0);
      const idx = parseInt(denteMesh.name.split("_")[1], 10);
      if (idx >= 1 && idx <= 16) direcao.negate();

      // 3) frontPoint e anchorPoint em world-space
      const frontWorld = centroWorld
        .clone()
        .addScaledVector(direcao, distanciaFrente);
      const anchorWorld = centroWorld
        .clone()
        .addScaledVector(direcao, -distanciaTras);

      // 4) monta o pivotGroup **sem** converter nada para local
      const parent = denteMesh.parent;
      const toothName = denteMesh.name;
      const pivotGroup = new THREE.Group();
      pivotGroup.name = `toothPivot_${toothName}`;
      pivotGroup.position.copy(anchorWorld); // <<< exatamente como no método completo
      pivotGroup.userData = {
        centroDente: centroWorld,
        pivotAxis: frontWorld.clone().sub(anchorWorld).normalize(),
        manualClickPoint: null,
      };

      // 5) “descola” o dente do parent e o reparenta ao pivotGroup
      parent.remove(denteMesh);
      const worldPos = new THREE.Vector3();
      denteMesh.getWorldPosition(worldPos);
      // mantém o mesmo offset que você usava antes:
      denteMesh.position.copy(worldPos.sub(anchorWorld));
      pivotGroup.add(denteMesh);

      // 6) desenha a linha do pivot (mesma lógica simplificada)
      const pts = [
        new THREE.Vector3(0, 0, 0),
        frontWorld.clone().sub(anchorWorld),
      ];
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: 0xff0000 })
      );
      line.name = `pivotLine_${toothName}`;
      pivotGroup.add(line);

      // 7) esferas de controle (igual antes)
      const sphGeo = new THREE.SphereGeometry(0.25);
      const sphAnc = new THREE.Mesh(
        sphGeo,
        new THREE.MeshBasicMaterial({ color: 0x0000ff })
      );
      const sphFro = new THREE.Mesh(
        sphGeo,
        new THREE.MeshBasicMaterial({ color: 0xff0000 })
      );
      const sphCentro = new THREE.Mesh(
        sphGeo,
        new THREE.MeshBasicMaterial({ color: 0xff00ff })
      );

      sphAnc.name = `sphereAncoragem_${toothName}`;
      sphFro.name = `sphereFrente_${toothName}`;
      sphCentro.name = `sphereCentro_${toothName}`;

      sphAnc.position.set(0, 0, 0);
      sphFro.position.copy(pts[1]);
      sphCentro.position.copy(centroWorld.clone().sub(anchorWorld));

      pivotGroup.add(sphAnc, sphFro, sphCentro);

      // 8) adiciona de volta e retorna
      parent.add(pivotGroup);
      return pivotGroup;
    },
    _removeToothShadows() {
      ["shadowUp", "shadowDown"].forEach((name) => {
        // procura o primeiro objeto com esse nome
        const mesh = this.scene.getObjectByName(name);
        if (mesh && mesh.parent) {
          mesh.parent.remove(mesh);
        }
      });
    },
    _createToothShadows(selectedTooth) {
      this._removeToothShadows();

      const make = (name, color) => {
        const clone = selectedTooth.clone();
        clone.name = name;
        clone.material = clone.material.clone();
        clone.material.transparent = true;
        clone.material.opacity = 0.1;
        clone.material.depthWrite = false;
        clone.material.color.set(color);
        this.scene.add(clone);
        return clone;
      };

      this.shadowUpMesh = make("shadowUp", 0x00ff00);
      this.shadowDownMesh = make("shadowDown", 0xff0000);

      // posicao e orientacao iniciais
      this.updateToothShadows();
    },
    updateToothShadows() {
      if (!this.shadowUpMesh || !this.shadowDownMesh) return;
      const selectedTooth = this.scene.getObjectByName(
        this.currentlySelectedTooth
      );
      if (!selectedTooth) return;

      const pivotGrp = selectedTooth.parent;
      const worldPos = selectedTooth.getWorldPosition(new THREE.Vector3());
      const worldQuat = pivotGrp.getWorldQuaternion(new THREE.Quaternion());
      const angle = this.previewAngle;

      // eixo “livre” baseado na câmera
      const camDir = this.camera.getWorldDirection(new THREE.Vector3());
      const abs = [camDir.x, camDir.y, camDir.z].map(Math.abs);
      const axisFree = new THREE.Vector3(
        abs[0] <= abs[1] && abs[0] <= abs[2] ? 1 : 0,
        abs[1] <= abs[0] && abs[1] <= abs[2] ? 1 : 0,
        abs[2] <= abs[0] && abs[2] <= abs[1] ? 1 : 0
      );
      pivotGrp.localToWorld(axisFree);
      pivotGrp.worldToLocal(axisFree);
      axisFree.normalize();

      if (this.isAltPressed) {
        // ─── TRANSLATION MODE ───
        const offsetUp = axisFree
          .clone()
          .multiplyScalar(+angle * this.translationFactor);
        const offsetDown = axisFree
          .clone()
          .multiplyScalar(-angle * this.translationFactor);
        this.shadowUpMesh.position.copy(worldPos.clone().add(offsetUp));
        this.shadowDownMesh.position.copy(worldPos.clone().add(offsetDown));
        // mantêm a rotação original
        this.shadowUpMesh.quaternion.copy(worldQuat);
        this.shadowDownMesh.quaternion.copy(worldQuat);
      } else {
        // para ambos os modos de rotação (pure e free)
        const axisRot = this.isShiftPressed
          ? pivotGrp.userData.pivotAxis.clone().normalize() // PURE
          : axisFree; // FREE

        const quatUp = worldQuat
          .clone()
          .multiply(new THREE.Quaternion().setFromAxisAngle(axisRot, +angle));
        const quatDown = worldQuat
          .clone()
          .multiply(new THREE.Quaternion().setFromAxisAngle(axisRot, -angle));

        // posição fixa
        this.shadowUpMesh.position.copy(worldPos);
        this.shadowDownMesh.position.copy(worldPos);

        // orientação atualizada
        this.shadowUpMesh.quaternion.copy(quatUp);
        this.shadowDownMesh.quaternion.copy(quatDown);
      }

      // renderiza para feedback imediato
      this.renderer.render(this.scene, this.camera);
    },
    highlightTooth(selectedTooth) {
      this._removeToothShadows();
      if (this.currentlySelectedTooth === selectedTooth.name) {
        this.toggleEditMode(false);

        // Desselecionar
        this.currentlySelectedTooth = null;
        this.selectedToothInfo = null;

        this.scene.traverse((object) => {
          // Restaurar opacidade dos dentes
          if (object.isMesh && object.name.startsWith("Tooth_")) {
            object.material.opacity = 1.0;
          }
          // Mostrar linha e esferas de todos os pivôs
          if (object.isGroup && object.name.startsWith("toothPivot_")) {
            object.children.forEach((child) => {
              if (
                child.name.startsWith("pivotLine_") ||
                child.name.startsWith("sphere")
              ) {
                child.visible = true;
              }
            });
          }
        });
      } else {
        // Selecionar novo dente
        this.selectedToothInfo = {
          name: selectedTooth.name,
          position: [
            selectedTooth.position.x,
            selectedTooth.position.y,
            selectedTooth.position.z,
          ],
          rotation: [
            selectedTooth.rotation._x, // Acesso direto ao ângulo X (radianos)
            selectedTooth.rotation._y, // Ângulo Y
            selectedTooth.rotation._z, // Ângulo Z
          ],
          scale: [
            selectedTooth.scale.x,
            selectedTooth.scale.y,
            selectedTooth.scale.z,
          ],
        };
        this.currentlySelectedTooth = selectedTooth.name;

        this.scene.traverse((object) => {
          // Aplicar opacidade aos dentes
          if (object.isMesh && object.name.startsWith("Tooth_")) {
            object.material.opacity = object === selectedTooth ? 0.8 : 0.3;
          }
          // Manipular visibilidade dos elementos de pivô
          if (object.isGroup && object.name.startsWith("toothPivot_")) {
            const isSelectedPivot =
              object.name === `toothPivot_${selectedTooth.name}`;
            object.children.forEach((child) => {
              // Mostrar/esconder apenas linha e esferas
              if (
                child.name.startsWith("pivotLine_") ||
                child.name.startsWith("sphere")
              ) {
                child.visible = isSelectedPivot;
              }
            });
          }
        });
        this._createToothShadows(selectedTooth);
        this.updateToothShadows();
      }
    },
    toggleEditMode(isEditing) {
      this.controls.enableZoom = !isEditing; // Desativa zoom no modo edição
      this.controls.update();
    },
    isVisibleInHierarchy(object) {
      let current = object;
      while (current) {
        if (!current.visible) return false;
        current = current.parent;
      }
      return true;
    },
    addInteraction() {
      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      window.addEventListener("mousedown", (event) => {
        if (event.button === 1) {
          // Botão do meio
          event.preventDefault();

          const rect = this.renderer.domElement.getBoundingClientRect();
          mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
          mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

          raycaster.setFromCamera(mouse, this.camera);

          const allDescendants = [];
          this.scene.traverse((child) => {
            if (
              child.isMesh &&
              this.isVisibleInHierarchy(child) &&
              !child.name.startsWith("shadowUp") &&
              !child.name.startsWith("shadowDown")
            ) {
              allDescendants.push(child);
            }
          });

          const intersects = raycaster.intersectObjects(allDescendants, false);

          if (intersects.length > 0) {
            const selectedObject = intersects[0].object;

            if (
              selectedObject.name.startsWith("Tooth_") ||
              selectedObject.name.startsWith("sphereFrente_")
            ) {
              const targetName = selectedObject.name.replace(
                "sphereFrente_",
                ""
              );
              const pivotGroup = this.scene.getObjectByName(
                `toothPivot_${targetName}`
              );
              const tooth =
                pivotGroup?.children.find(
                  (child) => child.name === targetName
                ) || selectedObject;
              const toothNumber = parseInt(targetName.replace("Tooth_", ""));
              const isLowerArch = toothNumber > 16;

              if (tooth) {
                const toothPos = tooth.getWorldPosition(new THREE.Vector3());
                this.controls.target.copy(toothPos);
                this.controls.update();

                const distance = this.camera.position.distanceTo(toothPos);

                // Configurações para cada visão
                const views = {
                  FRONTAL: {
                    position: new THREE.Vector3(
                      toothPos.x,
                      toothPos.y,
                      toothPos.z + distance
                    ),
                    up: new THREE.Vector3(0, 1, 0), // Y up
                    view: "FRONTAL",
                  },
                  BOTTOM: {
                    position: isLowerArch
                      ? new THREE.Vector3(
                          toothPos.x,
                          toothPos.y + distance,
                          toothPos.z
                        )
                      : new THREE.Vector3(
                          toothPos.x,
                          toothPos.y - distance,
                          toothPos.z
                        ),
                    up: isLowerArch
                      ? new THREE.Vector3(0, 0, 1)
                      : new THREE.Vector3(0, 0, -1),
                    view: "BOTTOM",
                  },
                  LATERAL: {
                    position: new THREE.Vector3(
                      toothPos.x + distance,
                      toothPos.y,
                      toothPos.z
                    ),
                    up: new THREE.Vector3(0, 1, 0), // Y up
                    view: "LATERAL",
                  },
                };

                // Sequência de ciclagem
                const cycleOrder = ["FRONTAL", "BOTTOM", "LATERAL"];
                const currentIndex = cycleOrder.indexOf(this.currentView);
                const nextView = cycleOrder[(currentIndex + 1) % 3];

                // Configura a nova visão
                const newView = views[nextView];
                this.currentView = newView.view;

                this.controls.enabled = false;

                // Animação suave com GSAP
                gsap.to(this.camera.position, {
                  x: newView.position.x,
                  y: newView.position.y,
                  z: newView.position.z,
                  duration: 1,
                  onStart: () => {
                    // Ajusta o "up" da câmera no início da animação
                    this.camera.up.copy(newView.up);
                    this.camera.lookAt(toothPos);
                  },
                  onUpdate: () => {
                    this.camera.lookAt(toothPos);
                    this.controls.update();
                  },
                  onComplete: () => {
                    this.controls.enabled = true;
                    this.camera.lookAt(toothPos);
                    this.controls.target.copy(toothPos);
                    this.controls.update();
                  },
                });
              }
            }
          }
        }
      });

      window.addEventListener("click", (event) => {
        const isShiftClick = event.shiftKey;
        const rect = this.renderer.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        raycaster.setFromCamera(mouse, this.camera);

        const allDescendants = [];
        this.scene.traverse((child) => {
          if (
            child.isMesh &&
            this.isVisibleInHierarchy(child) &&
            !child.name.startsWith("shadowUp") &&
            !child.name.startsWith("shadowDown")
          ) {
            allDescendants.push(child);
          }
        });

        const intersects = raycaster.intersectObjects(allDescendants, false);
        if (intersects.length === 0) return;

        const selectedObject = intersects[0].object;

        if (isShiftClick && selectedObject.name.startsWith("Tooth_")) {
          const toothName = selectedObject.name;
          const pointWorld = intersects[0].point.clone();

          // Armazena o ponto clicado manualmente
          toothClickPoints[toothName] = pointWorld;

          console.log(`Ponto salvo para ${toothName}:`, pointWorld);
          this.createPivotLine(selectedObject);
          return; // impede processamento normal
        }

        if (intersects.length > 0) {
          const selectedObject = intersects[0].object;

          // Caso 1: Clicou na esfera vermelha (frente)
          if (selectedObject.name.startsWith("sphereFrente_")) {
            // Extrair nome do dente do nome do grupo pivô (ex: "sphereFrente_Tooth_1" => "Tooth_1")
            const selectedToothName = selectedObject.name.replace(
              "sphereFrente_",
              ""
            );
            const toothPivotGroup = this.scene.getObjectByName(
              `toothPivot_${selectedToothName}`
            );

            if (toothPivotGroup) {
              // Encontrar o dente dentro do grupo de pivô
              const selectedTooth = toothPivotGroup.children.find(
                (child) => child.name === selectedToothName
              );
              if (selectedTooth) {
                this.highlightTooth(selectedTooth);
                this.toggleEditMode(true);
              }
            }

            // Caso 2: Clicou diretamente no dente (dentro do grupo de pivô)
          } else if (selectedObject.name.startsWith("Tooth_")) {
            const toothPivotGroup = selectedObject.parent;
            if (
              toothPivotGroup &&
              toothPivotGroup.name.startsWith("toothPivot_")
            ) {
              this.toggleEditMode(true);
              this.highlightTooth(selectedObject);
            }
          }
        }
      });
    },
    saveToothClickPointsJSON() {
      try {
        const jsonContent = JSON.stringify(toothClickPoints, null, 2);
        const blob = new Blob([jsonContent], { type: "application/json" });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "toothClickPoints.json";
        link.click();
        URL.revokeObjectURL(link.href); // limpeza opcional após uso

        console.log("Arquivo toothClickPoints.json salvo com sucesso.");
      } catch (e) {
        console.error("Erro ao salvar toothClickPoints:", e);
      }
    },
    async load3szFile(file) {
      const zip = new JSZip();
      const zipData = await zip.loadAsync(file);

      setTimeout(() => {
        this.$refs.progressDialog.showProgressDialog(this.$t("load3sz"), 10);
      }, 1000);

      // Identificar o rootFolder e os caminhos dos arquivos
      const firstFile = Object.keys(zip.files)[0];
      const rootFolder = firstFile.split("\\")[0];
      if (!rootFolder) return;
      this.patientName = rootFolder.replace(/\/$/, "");
      const modelsPath = `${this.patientName}\\setup\\Models\\`;

      const toothClickPointsFile = `${this.patientName}/setup/Models/toothClickPoints.json`;
      console.table(toothClickPointsFile);
      if (zipData.files[toothClickPointsFile]) {
        try {
          const jsonText = await zipData.files[toothClickPointsFile].async(
            "string"
          );
          toothClickPoints = JSON.parse(jsonText);
          console.log(
            "toothClickPoints carregado com sucesso:",
            toothClickPoints
          );
        } catch (error) {
          console.warn("Erro ao carregar toothClickPoints.json:", error);
        }
      } else {
        console.log("toothClickPoints.json não encontrado no pacote.");
        toothClickPoints = {}; // Garante estrutura vazia
      }

      const upperJawFile = `${modelsPath}Tooth_UpperJaw.stl`;
      const lowerJawFile = `${modelsPath}Tooth_LowerJaw.stl`;
      const toothFiles = [];

      for (let i = 1; i <= 32; i++) {
        const toothPath = `${modelsPath}Tooth_${i}.stl`;
        if (zipData.files[toothPath]) {
          toothFiles.push(toothPath);
        }
      }

      // Criar materiais
      const materialTeeth = new THREE.MeshPhysicalMaterial({
        color: 0xf7f7eb, // Cor base dos dentes (bege claro)
        roughness: 0.2, // Superfície lisa, mas não perfeitamente reflexiva
        metalness: 0.0, // Não metálico
        transmission: 0.2, // Translucidez
        ior: 1.4, // Índice de refração para simular vidro/dentes
        clearcoat: 1.0, // Camada de verniz para brilho extra
        clearcoatRoughness: 0.1, // Verniz suave
        envMapIntensity: 1.0, // Intensidade do reflexo ambiental
        flatShading: false, // Suaviza a superfície
        side: THREE.DoubleSide,
        transparent: true,
      });

      const materialGum = new THREE.MeshPhongMaterial({
        color: 0xf2b8c8, // rosa claro gengiva
        specular: 0x884444, // brilho suave, meio avermelhado
        emissive: 0x000000, // não emite luz própria
        shininess: 30, // um pouco de brilho, gengiva úmida
        side: THREE.DoubleSide, // renderiza ambos os lados
        transparent: true,
        opacity: 1, // totalmente opaco
        flatShading: false, // sombreamento suave
      });

      // Criar grupos identificáveis
      const upperGroup = new THREE.Group();
      upperGroup.name = "upperJaw";

      const lowerGroup = new THREE.Group();
      lowerGroup.name = "lowerJaw";

      const loader = new STLLoader();

      for (const filePath of toothFiles) {
        const stlData = await zipData.files[filePath].async("arraybuffer");
        const geometry = loader.parse(stlData);
        this.calculateVertexNormals(geometry);
        const mesh = new THREE.Mesh(geometry, materialTeeth.clone());

        // Definir nome para facilitar interatividade
        mesh.name = `Tooth_${filePath.match(/Tooth_(\d+)\.stl/)[1]}`;

        (parseInt(mesh.name.split("_")[1]) <= 16 ? upperGroup : lowerGroup).add(
          mesh
        );
      }

      var boundaryLoopsUpper;
      var boundaryLoopsLower;
      // Carregar gengivas
      if (zipData.files[upperJawFile]) {
        const upperGumData = await zipData.files[upperJawFile].async(
          "arraybuffer"
        );
        const geometry = loader.parse(upperGumData);
        geometry.computeVertexNormals();
        const boundaryPoints = detectBoundaries(geometry);
        boundaryLoopsUpper = groupBoundaryEdges(boundaryPoints);

        fillLoopsIntoMesh(geometry, boundaryLoopsUpper);
        this.calculateVertexNormals(geometry);
        const gumMesh = new THREE.Mesh(geometry, materialGum);

        gumMesh.name = "upper";

        // depois de carregar o gumMesh e antes de renderizar:
        const geom = gumMesh.geometry;
        const count = geom.attributes.position.count;

        // 1.1 um Float32Array zerado, um elemento por vértice
        const dispArray = new Float32Array(count);
        const dispAttr = new THREE.BufferAttribute(dispArray, 1);
        dispAttr.setUsage(THREE.DynamicDrawUsage); // diz ao Three que vai mudar sempre

        geom.setAttribute("displacement", dispAttr);

        // 1.2 injeta no material para usar no vertex shader
        gumMesh.material.onBeforeCompile = (shader) => {
          // declara o atributo
          shader.vertexShader = shader.vertexShader
            .replace(
              "void main() {",
              "attribute float displacement;\nvoid main() {"
            )
            // substitui o bloco de posicionamento
            .replace(
              "#include <begin_vertex>",
              `
            vec3 transformed = position + normal * displacement;
          `
            );
        };

        // Pré-processamento para deformação
        this.gumMorphing.upper = {
          mesh: gumMesh,
          neighbors: computeVertexNeighbors(geometry),
          restPositions: geometry.attributes.position.array.slice(),
          displacement: new Float32Array(
            geometry.attributes.position.array.length
          ), // Novo buffer por gengiva
          controlRings: createControlRings(geometry) || [], // Garante array vazio se falhar
          spatialHash: createSpatialHash(geometry.attributes.position.array), // ← Retorna { hash, cellSize }
          forces: new Float32Array(geometry.attributes.position.count).fill(0),
        };
        upperGroup.add(gumMesh);
      }

      if (zipData.files[lowerJawFile]) {
        const lowerGumData = await zipData.files[lowerJawFile].async(
          "arraybuffer"
        );
        const geometry = loader.parse(lowerGumData);
        geometry.computeVertexNormals();
        const boundaryPoints = detectBoundaries(geometry);
        boundaryLoopsLower = groupBoundaryEdges(boundaryPoints);

        fillLoopsIntoMesh(geometry, boundaryLoopsLower);
        this.calculateVertexNormals(geometry);
        const gumMesh = new THREE.Mesh(geometry, materialGum);
        gumMesh.name = "lower";

        // depois de carregar o gumMesh e antes de renderizar:
        const geom = gumMesh.geometry;
        const count = geom.attributes.position.count;

        // 1.1 um Float32Array zerado, um elemento por vértice
        const dispArray = new Float32Array(count);
        const dispAttr = new THREE.BufferAttribute(dispArray, 1);
        dispAttr.setUsage(THREE.DynamicDrawUsage); // diz ao Three que vai mudar sempre

        geom.setAttribute("displacement", dispAttr);

        // 1.2 injeta no material para usar no vertex shader
        gumMesh.material.onBeforeCompile = (shader) => {
          // declara o atributo
          shader.vertexShader = shader.vertexShader
            .replace(
              "void main() {",
              "attribute float displacement;\nvoid main() {"
            )
            // substitui o bloco de posicionamento
            .replace(
              "#include <begin_vertex>",
              `
            vec3 transformed = position + normal * displacement;
          `
            );
        };

        // Pré-processamento para deformação
        this.gumMorphing.lower = {
          mesh: gumMesh,
          neighbors: computeVertexNeighbors(geometry),
          restPositions: geometry.attributes.position.array.slice(),
          controlRings: createControlRings(geometry),
          displacement: new Float32Array(
            geometry.attributes.position.array.length
          ), // Novo buffer por gengiva
          spatialHash: createSpatialHash(geometry.attributes.position.array), // ← Retorna { hash, cellSize }
          forces: new Float32Array(geometry.attributes.position.count).fill(0),
        };

        lowerGroup.add(gumMesh);
      }

      // Adicionar à cena
      this.scene.add(upperGroup);
      this.scene.add(lowerGroup);

      // Adicionar interatividade
      this.addInteraction();
      this.addRotationOnScroll();

      if (Object.keys(toothClickPoints).length !== 0) {
        this.addPivotLinesForAllTeeth();
      }

      setTimeout(() => {
        this.$refs.progressDialog.cancelProcess();
      }, 2000);
    },
    updateGumSimulation(delta = 0.01) {
      const jaws = ["upper", "lower"];

      let anyValid = false;

      for (const jaw of jaws) {
        const gumData = this.gumMorphing[jaw];
        if (!gumData || !gumData.mesh || !gumData.mesh.geometry) continue;

        const { mesh, forces } = gumData;
        const geometry = mesh.geometry;
        const positionAttr = geometry.attributes.position;
        const normalAttr = geometry.attributes.normal;

        if (!positionAttr || !normalAttr || !forces) continue;

        const vertex = new THREE.Vector3();
        const normal = new THREE.Vector3();

        for (let i = 0; i < positionAttr.count; i++) {
          const force = forces[i];
          if (Math.abs(force) < 0.00001) continue;

          vertex.fromBufferAttribute(positionAttr, i);
          normal.fromBufferAttribute(normalAttr, i);

          vertex.addScaledVector(normal, force * delta);
          positionAttr.setXYZ(i, vertex.x, vertex.y, vertex.z);

          forces[i] *= 0.9;
        }

        positionAttr.needsUpdate = true;
        geometry.computeVertexNormals();

        anyValid = true;
      }

      // Só continua se houver ao menos uma gengiva válida carregada
      if (anyValid) {
        requestAnimationFrame(() => this.updateGumSimulation(delta));
      }
    },
    rotateAllMeshesToXZ() {
      const rotationMatrix = new THREE.Matrix4();
      const axis = new THREE.Vector3(1, 0, 0); // eixo Z
      rotationMatrix.makeRotationAxis(axis, -Math.PI / 2); // -90 graus

      this.scene.traverse((object) => {
        if (object.isMesh && object.geometry) {
          object.geometry.applyMatrix4(rotationMatrix);
          object.geometry.computeBoundingBox();
          object.geometry.computeVertexNormals();
        }
      });
    },
    async generateRotatedImages() {
      const renderer = this.renderer; // Certifique-se de que seu renderizador Three.js está configurado
      const scene = this.scene; // Sua cena do Three.js
      const camera = this.camera; // Sua câmera
      const step = THREE.MathUtils.degToRad(45); // Converter graus para radianos
      const rotations = []; // Lista de rotações (x, y, z)

      // Gerar combinações de rotações
      for (let x = 0; x < 2 * Math.PI; x += step) {
        for (let y = 0; y < 2 * Math.PI; y += step) {
          for (let z = 0; z < 2 * Math.PI; z += step) {
            rotations.push({ x, y, z });
          }
        }
      }

      // Garantir o tamanho do renderizador
      renderer.setSize(512, 512); // Tamanho das imagens
      renderer.setPixelRatio(window.devicePixelRatio);

      // Iterar sobre as rotações e capturar imagens
      for (const [index, rotation] of rotations.entries()) {
        // Aplicar a rotação na cena ou no objeto principal
        scene.rotation.set(rotation.x, rotation.y, rotation.z);

        // Renderizar a cena
        renderer.render(scene, camera);

        // Obter a imagem do canvas como base64
        const imageURL = renderer.domElement.toDataURL("image/png");

        // Baixar ou salvar o arquivo
        await this.saveImageAsync(imageURL, `scene_${index}.png`);
        // Aguardar o intervalo antes de continuar
        await this.delay(300);
      }

      alert("Imagens geradas com sucesso!");
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
  },
};
